import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/DatePicker.css';

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Select,
    MenuItem,
    Chip,
    Button,
    IconButton,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    TextField,
    FormControl,
    InputLabel,
    CircularProgress,
    Autocomplete,
    Checkbox,
    ListItemText,
    Menu,
    Grid,
    Stack,
    Divider,
    TableSortLabel,
    Popper,
    InputAdornment,
} from '@mui/material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import {
    Assignment as AssignmentIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    Clear as ClearIcon,
    Save as SaveIcon,
    LocationOn as LocationOnIcon,
    FileDownload as FileDownloadIcon,
    PictureAsPdf as PictureAsPdfIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    HelpOutline as HelpOutlineIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    UnfoldMore as UnfoldMoreIcon,
    ViewColumn as ViewColumnIcon,
    Warning as WarningIcon,
    Cancel as CancelIcon,
    RemoveCircle as RemoveCircleIcon,
    Block as BlockIcon,
    TaskAlt as TaskAltIcon,
    CheckCircle as CheckCircleIcon,
    List as ListIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { TableVirtuoso, GroupedTableVirtuoso } from 'react-virtuoso';
import { useDispatch, useSelector } from 'react-redux';
import { $authHost } from '../../http';
import { ktpIcon, pillarIcon, projectStatusOreolIcon } from '../layers/icons';
import '../../utils/arial-normal.js'
import '../../utils/arial-bold.js'
import { selectAccountingsGroupedByKtpNew, selectAccountingsGroupedByKtpNewWithoutAnyFilters, selectRulesMap, selectChecklistFilter } from '../../store/selectors/supervisionSelector.js';
import { fetchAccounting, toggleDarkMode } from '../../store/reducers/stateReducer.js';
import { AdvancedFiltersComponent } from './common/AdvancedFilters.jsx';
// Регистрируем русскую локаль
registerLocale('ru', ru);

export const codeColors = {
    'П': [147, 51, 234],
    'Г': [16, 185, 129],
    'С': [59, 130, 246],
    'Р': [246, 34, 58]
};

export const getStatusIcon = (iconName, size = 18) => {
    const iconMap = {
        'warning': <WarningIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />,
        'cancel': <CancelIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />,
        'remove_circle': <RemoveCircleIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />,
        'block': <BlockIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />,
        'task_alt': <TaskAltIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />,
        'check_circle': <CheckCircleIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />,
        'list': <ListIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />
    };
    return iconMap[iconName] || <ListIcon color="white" sx={{ fontSize: size, verticalAlign: 'middle' }} />;
};


function formatDateShort(str) {
    return formatDate(str, true)
}

function formatDate(str, short = false) {
    if (!str) return '';

    // Если дата в формате "YYYY-MM-DD" (из базы данных)
    if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [, year, month, day] = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата уже в формате "DD-MM-YYYY"
    if (str.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [, day, month, year] = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата в формате "HH:MM DD-MM-YYYY"
    if (str.match(/^\d{2}:\d{2}\s\d{2}-\d{2}-\d{4}$/)) {
        const [, hours, minutes, day, month, year] = str.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        return `${hours}:${minutes} ${day}.${month}.${year}`;
    }

    // Попытка парсинга как обычной даты
    try {
        const date = new Date(str);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return short ? `${day}.${month}.${year}` : `${hours}:${minutes} ${day}.${month}.${year}`;
    } catch {
        return '';
    }
}

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        // Если дата уже в формате "YYYY-MM-DD" (из базы данных)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
        }

        // Если дата в формате "HH:MM DD-MM-YYYY"
        const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        if (dateTimeMatch) {
            const [, hours, minutes, day, month, year] = dateTimeMatch;
            return `${year}-${month}-${day}`;
        }

        // Если дата в формате "DD-MM-YYYY"  
        const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dateMatch) {
            const [, day, month, year] = dateMatch;
            return `${year}-${month}-${day}`;
        }

        // Попытка парсинга как обычной даты
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

// Функция для форматирования даты из input в формат "YYYY-MM-DD" для базы данных
const formatDateFromInput = (inputValue) => {
    if (!inputValue) return '';
    try {
        const date = new Date(inputValue);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const columns = [
    {
        id: 'id', label: '№', minWidth: 50, width: 50,
        align: 'center',
    },
    {
        id: 'pillar', label: 'Номер опоры', minWidth: 150, width: 200,
        align: 'center',
    },
    {
        id: 'violations', label: 'Выявленные отступления от ПСД', minWidth: 250, width: 300,
        align: 'center',
    },
    {
        id: 'instructions', label: 'Указания и сроки их выполнения', minWidth: 250, width: 300,
        align: 'center',
    },
    {
        id: 'cost', label: 'Стоимость', minWidth: 90, width: 90,
        align: 'center',
    },
    {
        id: 'status', label: 'Отметка о выполнении', minWidth: 100, width: 200,
        align: 'center',
    },
    {
        id: 'created', label: 'Авторский надзор', minWidth: 100, width: 200,
        align: 'center',
    }
];

const VirtuosoTableComponents = {

    Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            width: '100%',
            minWidth: '100%',
            // overflowX: 'auto', // для скролла
        }} />
    )),
    Table: (props) => (
        <Table {...props} sx={{
            borderCollapse: 'separate',
            tableLayout: 'auto', // Автоматическое распределение ширины
            bgcolor: 'transparent',
            width: '100%', // Используем всю доступную ширину
            minWidth: '100%', // Минимальная ширина 100%
        }} />
    ),
    TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};

function fixedHeaderContent(darkMode, editMode, selectedRows, filteredData, handleSelectAll, sortBy, sortDirection, handleSort, visibleColumns, columnWidths) {

    // Функция для получения иконки сортировки
    const getSortIcon = (columnId) => {
        if (sortBy !== columnId) {
            return null
            // return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
        }
        return sortDirection === 'asc'
            ? <ArrowUpwardIcon fontSize="small" />
            : <ArrowDownwardIcon fontSize="small" />;
    };

    // Получаем только видимые столбцы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);

    return (
        <>
            <TableRow>
                {visibleColumnsArray.map((column, index) => (
                    <TableCell
                        key={column.id}
                        variant="head"
                        align={column.align || ''}
                        style={{
                            width: columnWidths[column.id] || column.minWidth,
                            minWidth: column.minWidth,
                            maxWidth: columnWidths[column.id] || column.minWidth
                        }}
                        colSpan={column.colSpan}
                        sx={{
                            bgcolor: darkMode ? '#a94442' : '#CD5C5C',
                            color: darkMode ? 'white' : 'black',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            borderBottom: darkMode
                                ? '2px solid rgba(255, 255, 255, 0.12)'
                                : '2px solid rgba(0, 0, 0, 0.12)',
                            borderRight: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(0, 0, 0, 0.08)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            p: '4px 8px',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: darkMode ? '#b94442' : '#DC5C5C',
                            }
                        }}
                        onClick={() => handleSort(column.id)}
                    >
                        <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}

                        >
                            {index === 0 && editMode ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Checkbox
                                        checked={selectedRows.size > 0 && selectedRows.size === filteredData.length}
                                        indeterminate={selectedRows.size > 0 && selectedRows.size < filteredData.length}
                                        onChange={(e) => {
                                            e.stopPropagation();
                                            handleSelectAll(e.target.checked);
                                        }}
                                        size="small"
                                        sx={{
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                            p: '2px',
                                            '&.Mui-checked': {
                                                color: darkMode ? '#64b5f6' : '#1976d2',
                                            },
                                            '&.MuiCheckbox-indeterminate': {
                                                color: darkMode ? '#64b5f6' : '#1976d2',
                                            },
                                        }}
                                    />
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                                        {column.label}
                                    </Typography>
                                    {/* {getSortIcon(column.id)} */}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    {column.tooltip ? (
                                        <Tooltip title={column.tooltip}>
                                            <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                                {column.label}
                                            </Typography>
                                        </Tooltip>
                                    ) : (
                                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                            {column.label}
                                        </Typography>
                                    )}
                                    {/* {getSortIcon(column.id)} */}
                                </Box>
                            )}
                        </TableSortLabel>
                    </TableCell>

                ))}
            </TableRow>
            {/* Второй ряд заголовков для подколонок дат */}
            {/* <TableRow>
        <TableCell sx={{
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}></TableCell>
        <TableCell sx={{
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}></TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#64b5f6' : '#1976d2',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>План</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#4caf50' : '#2e7d32',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>Факт</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#64b5f6' : '#1976d2',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>План</TableCell>
        <TableCell align="center" sx={{
          minWidth: 85,
          bgcolor: darkMode ? '#2b2b2b' : '#fff',
          color: darkMode ? '#4caf50' : '#2e7d32',
          fontWeight: 500,
          fontSize: '0.75rem',
          borderBottom: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.12)' 
            : '1px solid rgba(0, 0, 0, 0.12)',
          borderRight: darkMode 
            ? '1px solid rgba(255, 255, 255, 0.08)' 
            : '1px solid rgba(0, 0, 0, 0.08)',
          position: 'sticky',
          top: 57,
          zIndex: 10,
        }}>Факт</TableCell>
        {[...Array(6)].map((_, index) => (
          <TableCell key={index} sx={{
            bgcolor: darkMode ? '#2b2b2b' : '#fff',
            borderBottom: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.12)' 
              : '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: darkMode 
              ? '1px solid rgba(255, 255, 255, 0.08)' 
              : '1px solid rgba(0, 0, 0, 0.08)',
            position: 'sticky',
            top: 57,
            zIndex: 10,
          }}></TableCell>
        ))}
      </TableRow> */}
        </>
    );
}

// Функция для отображения заголовка группы
function groupContent(index, groupedData, darkMode, user, activeAccoutingsProjects, rulesMap, visibleColumns) {
    if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
        return null;
    }
    // console.log(index, groupedData, darkMode, user, handleOpenCreateDialog, objectsList)
    // Находим первый элемент группы для получения названия КДП
    const firstItemInGroup = groupedData.groupedData.find(item => item.group === index);
    const groupName = firstItemInGroup?.groupName || `Группа ${index}`;
    const groupCount = groupedData.groupCounts[index];
    const groupCost = groupedData.groupedData.filter(item => item.group === index).flatMap((p) => p.accounting?.checklist || []).reduce((sum, cur) => sum + (Number(rulesMap[cur]?.cost) || 0), 0)

    // Находим KDP ID для этой группы
    // const itemKdpId = firstItemInGroup?.project_id;
    // Если KDP ID не найден в объекте, ищем по названию группы в списке KDP
    // const groupKdpId = itemKdpId || objectsList.find(p => p.name === groupName)?.id;

    // Получаем только видимые столбцы для определения структуры группы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);
    const cells = [];

    // Определяем соответствие столбцов их позициям
    const columnPositions = {
        'id': 0,
        'pillar': 1,
        'violations': 2,
        'instructions': 3,
        'cost': 4,
        'status': 5,
        'created': 6
    };

    visibleColumnsArray.forEach((column, index) => {
        const baseStyle = {
            backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0',
            color: darkMode ? '#a94442' : '#CD5C5C',
            fontWeight: 600,
            fontSize: '0.9rem',
            padding: '4px 16px',
            borderBottom: darkMode
                ? '2px solid rgba(169, 68, 66, 1)'
                : '2px solid rgba(205, 92, 92, 1)',
        };

        switch (column.id) {
            case 'id':
                cells.push(
                    <td key={column.id} colSpan={1} align='center' style={baseStyle}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {groupCount}
                        </Typography>
                    </td>
                );
                break;

            case 'pillar':
                // Определяем colspan для названия объекта в зависимости от видимых столбцов
                let nameColspan = 1;
                const nextColumns = visibleColumnsArray.slice(index + 1);

                // Если следующие столбцы это violations и instructions, объединяем их
                if (nextColumns.length >= 2 &&
                    nextColumns[0].id === 'violations' &&
                    nextColumns[1].id === 'instructions') {
                    nameColspan = 3;
                } else if (nextColumns.length >= 1 &&
                    (nextColumns[0].id === 'violations' || nextColumns[0].id === 'instructions')) {
                    nameColspan = 2;
                }

                cells.push(
                    <td key={column.id} colSpan={nameColspan} style={baseStyle}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Объект: {groupName}
                        </Typography>
                    </td>
                );
                break;

            case 'cost':
                cells.push(
                    <td key={column.id} colSpan={1} align='center' style={baseStyle}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {new Intl.NumberFormat('ru-RU', {
                                style: 'currency',
                                currency: 'RUB',
                            }).format(groupCost || 0)}
                        </Typography>
                    </td>
                );
                break;

            default:
                // Для остальных столбцов создаем пустые ячейки
                if (column.id !== 'violations' && column.id !== 'instructions') {
                    cells.push(
                        <td key={column.id} colSpan={1} style={{ ...baseStyle, fontSize: '0.8rem' }}>
                        </td>
                    );
                }
                break;
        }
    });

    return cells;
    {/* <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}> */}
    {/* Кнопка создания объекта в группе */ }
    {/*     {user.roles?.includes('project_manager') && (
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenCreateDialog(groupKdpId)}
                            sx={{
                                bgcolor: '#4caf50',
                                color: 'white',
                                px: 1.5,
                                py: 0.5,
                                textTransform: 'none',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                borderRadius: 1.5,
                                boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                '&:hover': {
                                    bgcolor: '#388e3c',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            Добавить
                        </Button>
                    )} */}

    {/* </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>



                </Box> */}
    {/* </Box> */ }

}

const fieldColors = {
    name: { light: '#2e7d32', dark: '#4caf50' },
    dates: { light: '#1976d2', dark: '#64b5f6' },
    stage: { light: '#6a1b9a', dark: '#ab47bc' },
    responsible: { light: '#d32f2f', dark: '#f44336' },
    coexecutors: { light: '#f57c00', dark: '#ffb74d' },
    equipment: { light: '#3949ab', dark: '#5c6bc0' },
};

const EditableSelectCell = ({ id, field, value, label, saveEditedField, darkMode, options, multiple = false }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'responsible':
                return {
                    bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                    color: darkMode ? '#f44336' : '#d32f2f',
                    selectedBg: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                };
            case 'coexecutors':
                return {
                    bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                    color: darkMode ? '#ffb74d' : '#f57c00',
                    selectedBg: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                };
            case 'equipment':
                return {
                    bgcolor: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                    color: darkMode ? '#5c6bc0' : '#3949ab',
                    selectedBg: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                };
            case 'stage':
                return {
                    bgcolor: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                    color: darkMode ? '#ab47bc' : '#6a1b9a',
                    selectedBg: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                };
            default:
                return {
                    bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                    color: darkMode ? '#ce93d8' : '#9c27b0',
                    selectedBg: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value || (multiple ? [] : null));
    const [inputValue, setInputValue] = useState('');

    const handleChange = (event, newValue) => {
        setLocalValue(newValue);
    };

    const handleInputChange = (event, newInputValue) => {
        setInputValue(newInputValue);
    };

    const handleBlur = () => {
        saveEditedField({
            rowId: id,
            field: field,
            value: localValue,
            isValueChanged: JSON.stringify(value) !== JSON.stringify(localValue)
        });
    };

    const handleClear = () => {
        setLocalValue(multiple ? [] : null);
    };

    const handleSave = () => {
        handleBlur();
    };

    const getOptionLabel = (option) => {
        return option?.name || option?.display_name || String(option || '');
    };

    const isOptionEqualToValue = (option, value) => {
        return option?.id === value?.id;
    };

    return (
        <Autocomplete
            value={localValue}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            options={options || []}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            multiple={multiple}
            size="small"
            autoFocus
            disableClearable={true}
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            disableCloseOnSelect={multiple}
            renderInput={(params) => (
                <TextField
                    {...params}
                    // placeholder={label}
                    size="small"
                    // InputProps={{
                    //     ...params.InputProps,
                    //     endAdornment: (
                    //         <Box sx={{
                    //             position: multiple ? 'absolute' : '',

                    //             bottom: '5px',
                    //             right: '10px',
                    //             display: 'flex',
                    //             alignItems: 'center',
                    //             gap: 0.5
                    //         }}>
                    //             {multiple && localValue.length > 0 && (
                    //                 <IconButton
                    //                     size="small"
                    //                     onClick={(e) => {
                    //                         e.stopPropagation();
                    //                         handleClear();
                    //                     }}
                    //                     edge="end"
                    //                     tabIndex={-1}
                    //                     sx={{
                    //                         color: darkMode ? '#f44336' : '#d32f2f',
                    //                         p: 0.3,
                    //                         '&:hover': {
                    //                             bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                    //                         },
                    //                     }}
                    //                 >
                    //                     <ClearIcon fontSize="small" />
                    //                 </IconButton>
                    //             )}
                    //             <IconButton
                    //                 size="small"
                    //                 onClick={(e) => {
                    //                     e.stopPropagation();
                    //                     handleSave();
                    //                 }}
                    //                 edge="end"
                    //                 tabIndex={-1}
                    //                 sx={{
                    //                     color: darkMode ? '#4caf50' : '#2e7d32',
                    //                     p: 0.3,
                    //                     '&:hover': {
                    //                         bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                    //                     }
                    //                 }}
                    //             >
                    //                 <SaveIcon fontSize="small" />
                    //             </IconButton>
                    //         </Box>
                    //     ),
                    // }}
                    sx={{
                        '& .MuiOutlinedInput-input': {

                            cursor: 'text',
                            color: darkMode ? '#ffffff' : '#333',
                            fontSize: '0.875rem',
                            width: 'fit-content !important',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: fieldColors.color,
                            borderWidth: '2px',
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                        },
                        '& .MuiInputLabel-root': {
                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                            fontSize: '0.875rem',
                            '&.Mui-focused': {
                                color: fieldColors.color,
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                            borderRadius: '4px',
                        },
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        '& .MuiInputBase-root': {
                            p: '5px !important',
                            borderRadius: '4px',
                        },
                        '& .MuiInputBase-input': {

                            fontWeight: 500,
                            fontSize: '0.8rem',
                            borderRadius: '4px',
                        },
                    }}
                />
            )}
            renderOption={(props, option) => (
                <Box component="li" {...props}>
                    <Typography variant="body2">
                        {getOptionLabel(option)}
                    </Typography>
                </Box>
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={getOptionLabel(option)}
                        onClick={() => { }}
                        size="small"
                        sx={{
                            bgcolor: fieldColors.bgcolor,
                            color: fieldColors.color,
                            fontSize: '0.7rem',
                            height: 'auto',
                            minHeight: '20px',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: '1.2',
                                padding: '2px 6px',
                            },
                        }}
                    />
                ))
            }
            sx={{
                '& .MuiAutocomplete-popper': {
                    zIndex: 1300,
                },
                '& .MuiAutocomplete-paper': {
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                    '& .MuiAutocomplete-option': {
                        color: darkMode ? '#f5f7fa' : '#333',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                        },
                        '&[aria-selected="true"]': {
                            bgcolor: fieldColors.selectedBg,
                        },
                    },
                },
            }}
        />
    );
};

const EditableNumberCell = ({ id, field, value, label, saveEditedField, darkMode }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'stage':
                return {
                    color: darkMode ? '#ab47bc' : '#6a1b9a',
                };
            case 'fid':
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
            default:
                return {
                    color: darkMode ? '#9c27b0' : '#9c27b0',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value || '');

    const handleNumberChange = (e) => {
        const inputValue = e.target.value;
        // Разрешаем только цифры и пустую строку
        if (inputValue === '' || /^\d+$/.test(inputValue)) {
            setLocalValue(inputValue);
        }
    };

    const handleBlur = () => {
        const numericValue = localValue ? parseInt(localValue, 10) : value;
        saveEditedField({
            rowId: id,
            field: field,
            value: numericValue,
            isValueChanged: value !== numericValue
        });
    };

    const handleClear = () => {
        setLocalValue('');
    };

    const handleSave = () => {
        handleBlur();
    };

    return (
        <TextField
            fullWidth
            size="small"
            value={localValue}
            onChange={handleNumberChange}
            onBlur={handleBlur}
            autoFocus

            sx={{
                '& .MuiOutlinedInput-input': {
                    p: '5px',
                    cursor: 'text',
                    color: darkMode ? '#ffffff' : '#333',
                    fontSize: '0.875rem',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldColors.color,
                    borderWidth: '2px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                },
                '& .MuiInputLabel-root': {
                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                    fontSize: '0.875rem',
                    '&.Mui-focused': {
                        color: fieldColors.color,
                    }
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                    borderRadius: '4px',
                },
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                '& .MuiInputBase-root': {
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    borderRadius: '4px',
                    minWidth: '60px',
                    // width: '100%'
                },
                '& .MuiInputBase-input': {
                    borderRadius: '4px',
                },
            }}
        />
    );
};

const EditableDateCell = ({ id, field, value, label, saveEditedField, darkMode }) => {
    // Определяем цвета в зависимости от поля
    const getFieldColors = (field) => {
        switch (field) {
            case 'plan_start':
            case 'fact_start':
            case 'plan_end':
            case 'fact_end':
                return {
                    color: darkMode ? '#64b5f6' : '#1976d2',
                };
            default:
                return {
                    color: darkMode ? '#1976d2' : '#1976d2',
                };
        }
    };

    const fieldColors = getFieldColors(field);
    const [localValue, setLocalValue] = useState(value)

    // Преобразуем строку даты в объект Date для DatePicker
    const getDateFromString = (dateString) => {
        if (!dateString) return null;
        try {
            // Если дата в формате "YYYY-MM-DD" (из базы данных)
            if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return new Date(dateString);
            }

            // Если дата в формате "HH:MM DD-MM-YYYY"
            const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
            if (dateTimeMatch) {
                const [, hours, minutes, day, month, year] = dateTimeMatch;
                return new Date(`${year}-${month}-${day}`);
            }

            // Если дата в формате "DD-MM-YYYY"  
            const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (dateMatch) {
                const [, day, month, year] = dateMatch;
                return new Date(`${year}-${month}-${day}`);
            }

            // Попытка парсинга как обычной даты
            const date = new Date(dateString);
            return isNaN(date.getTime()) ? null : date;
        } catch {
            return null;
        }
    };

    const handleDateChange = (date) => {
        if (date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formattedDate = `${year}-${month}-${day}`;
            setLocalValue(formattedDate);
        } else {
            setLocalValue('');
        }
    };

    const handleBlur = () => {
        saveEditedField({ rowId: id, field: field, value: localValue, isValueChanged: value !== localValue });

    };

    return (
        <Box sx={{ position: 'relative' }}>
            {(() => {
                try {
                    return (
                        <DatePicker
                            selected={getDateFromString(localValue)}
                            // onCalendarClose={handleDateChange}
                            onChange={handleDateChange}
                            onBlur={handleBlur}
                            onCalendarClose={handleBlur}
                            // onClickOutside={handleBlur}

                            locale="ru"
                            dateFormat="dd.MM.yyyy"
                            // placeholderText={label}
                            autoFocus
                            // shouldCloseOnSelect={false}

                            // isClearable
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            yearDropdownItemNumber={10}
                            scrollableYearDropdown
                            autoComplete="off"
                            customInput={
                                <TextField
                                    // fullWidth
                                    // label={label}
                                    size="small"
                                    // variant="standard"
                                    // InputProps={{
                                    //     endAdornment: (
                                    //         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    //             {localValue && (
                                    //                 <IconButton
                                    //                     size="small"
                                    //                     onClick={e => {
                                    //                         e.stopPropagation();
                                    //                         setLocalValue('');
                                    //                     }}
                                    //                     edge="end"
                                    //                     tabIndex={-1}
                                    //                     sx={{
                                    //                         color: darkMode ? '#f44336' : '#d32f2f',
                                    //                         p: 0.1,
                                    //                         '&:hover': {
                                    //                             bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                    //                         },
                                    //                     }}
                                    //                 >
                                    //                     <ClearIcon fontSize="small" />
                                    //                 </IconButton>
                                    //             )}
                                    //             <IconButton
                                    //                 size="small"
                                    //                 onClick={e => {
                                    //                     e.stopPropagation();
                                    //                     handleBlur();
                                    //                 }}
                                    //                 edge="end"
                                    //                 tabIndex={-1}
                                    //                 sx={{
                                    //                     color: darkMode ? '#4caf50' : '#2e7d32',
                                    //                     p: 0.1,
                                    //                     '&:hover': {
                                    //                         bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(46, 125, 50, 0.1)',
                                    //                     }
                                    //                 }}
                                    //             >
                                    //                 <SaveIcon fontSize="small" />
                                    //             </IconButton>
                                    //         </Box>
                                    //     ),
                                    // }}
                                    sx={{
                                        '& .MuiOutlinedInput-input': {
                                            p: '5px',
                                            cursor: 'pointer',
                                            color: darkMode ? '#ffffff' : '#333',
                                            fontSize: '0.875rem',
                                        },
                                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: fieldColors.color,
                                            borderWidth: '2px',
                                        },
                                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                            fontSize: '0.875rem',
                                            '&.Mui-focused': {
                                                color: fieldColors.color,
                                            }
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                            borderRadius: '4px',
                                        },
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        '& .MuiInputBase-root': {
                                            // pr: 0,
                                            fontWeight: 500,
                                            fontSize: '0.8rem',
                                            borderRadius: '4px',
                                            // minWidth: '140px',
                                            width: '90px'
                                        },
                                        '& .MuiInputBase-input': {
                                            borderRadius: '4px',
                                        },
                                    }}
                                />
                            }
                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                        />
                    );
                } catch (error) {
                    console.error('DatePicker error:', error);
                    return null;
                }
            })()}
        </Box>
    );
}

// Функция для открытия обзора опоры в новой вкладке
const openObjectView = (object) => {
    if (!object.coords && !object.x_y_coord) {
        console.warn('Нет координат для опоры:', object);
        return;
    }

    // Определяем координаты
   
    // Определяем тип опоры на основе type объекта или используем дефолтный 'pillar'
    const pillarType = object.type || 'pillar';
    
    // Создаем base64 строку с данными объекта
    const base64 = btoa(JSON.stringify({
        type: pillarType,
        id: object.fid
    }));

    // Формируем URL с параметром view
    const url = `${window.location.origin}/an?view=${encodeURIComponent(base64)}`;

    // Открываем новую вкладку
    window.open(url, '_blank');
};


export const statuses = {
    'Актуальные': {
        color: '#F19E39',
        argb: 'FFF19E39',
        icon: 'warning',
        text: 'Актуальные',
        filter: ['Не устранено', 'Частично устранено'],
        next: 'Не устранено'
    },
    'Не устранено': {
        color: '#EC4137',
        argb: 'FFEC4137',
        icon: 'cancel',
        filter: ['Не устранено'],
        text: 'Не устранено',
        next: 'Частично устранено'
    },
    'Частично устранено': {
        color: '#FDBE0E',
        argb: 'FFFDBE0E',
        icon: 'remove_circle',
        filter: ['Частично устранено'],
        text: 'Частично устранено',
        next: 'Отклонено'
    },
    'Отклонено': {
        color: '#9C27B0',
        argb: 'FF9C27B0',
        icon: 'block',
        filter: ['Отклонено'],
        text: 'Отклонено',
        next: 'Готово к проверке'
    },
    'Готово к проверке': {
        color: '#2196F3',
        argb: 'FF2196F3',
        icon: 'task_alt',
        filter: ['Готово к проверке'],
        text: 'Готово к проверке',
        next: 'Устранено'
    },
    'Устранено': {
        color: '#48B349',
        argb: 'FF48B349',
        icon: 'check_circle',
        filter: ['Устранено'],
        text: 'Устранено',
        next: 'Отобразить все'
    },
    'Отобразить все': {
        color: '#607D8B',
        argb: 'FF607D8B',
        icon: 'list',
        filter: [],
        text: 'Отобразить все',
        next: 'Актуальные'
    }
};

// Функция-обертка для фильтрации видимых ячеек
function filterVisibleCells(cells, visibleColumns) {
    // Получаем только видимые столбцы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);

    // Если все столбцы видимы, возвращаем все ячейки
    if (visibleColumnsArray.length === columns.length) {
        return cells;
    }

    // Фильтруем ячейки по индексу видимых столбцов
    return cells.filter((cell, index) => {
        // Индекс соответствует порядку столбцов в массиве columns
        return index < columns.length && visibleColumns[columns[index].id];
    });
}

function rowContent(_index, row, { editMode, editing, setEditingField, saveEditedField, darkMode, editedFields, selectedRows, handleRowSelection, rulesMap, canEditReady, canEditSuccess, visibleColumns }) {
    // Если это padding элемент, возвращаем пустую строку
    if (row.isPadding) {
        // Получаем только видимые столбцы
        const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);
        return (
            <React.Fragment>
                {visibleColumnsArray.map((column) => (
                    <TableCell key={column.id} sx={{ height: '1px', padding: 0, border: 'none' }} />
                ))}
            </React.Fragment>
        );
    }

    // Определяем цвет фона для чередования строк (как в AccountingSheetContent)
    const isRowEven = _index % 2 !== 0;
    const rowBackgroundColor = isRowEven ? { backgroundColor: darkMode ? '#1a0101' : '#FFE4E1' } : {};

    const rowStatus = editedFields.status || row.accounting?.status || null

    if (!rowStatus) return null;

    let status = rowStatus;
    // if (rowStatus && Object.keys(translate).includes(rowStatus)) {
    //     status = statuses[translate[rowStatus]];
    // }
    if (!status) return null;

    let color = statuses[status]?.color || '#000000';
    let icon = statuses[status]?.icon || 'list';

    // Проверяем, что rulesMap доступен
    if (!rulesMap) {
        console.warn('rulesMap is not available in rowContent');
        return null;
    }

    // const hasChecklist = row.accounting.checklist && row.accounting.checklist.length > 0;

    // Вычисляем количество видимых checklist с учетом фильтрации
    // let visibleChecklistCount = 1;
    // if (hasChecklist) {
    //     const part_elimin_categ = row.accounting.part_elimin_categ || { ready: [], success: [], rejected: [] };
    //     visibleChecklistCount = row.accounting.checklist.filter(c => {
    //         if (!rulesMap[c]) return false;
    //         const isSuccessChecked = part_elimin_categ.success?.includes(c) || false;
    //         return !(hideCompletedChecklist && isSuccessChecked);
    //     }).length;
    // }

    // console.log(row)
    const getStageColor = (stage) => {
        const stageStr = String(stage || '');
        switch (stageStr) {
            case 'Проектирование':
                return { bg: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)', color: darkMode ? '#ffb74d' : '#f57c00' };
            case 'Строительство':
                return { bg: darkMode ? 'rgba(33, 150, 243, 0.2)' : 'rgba(33, 150, 243, 0.1)', color: darkMode ? '#64b5f6' : '#1976d2' };
            case 'Завершено':
                return { bg: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)', color: darkMode ? '#81c784' : '#388e3c' };
            default:
                return { bg: darkMode ? 'rgba(158, 158, 158, 0.2)' : 'rgba(158, 158, 158, 0.1)', color: darkMode ? '#bdbdbd' : '#616161' };
        }
    };

    const stageColors = getStageColor(row.stage);

    // Проверяем, была ли строка изменена
    const isRowEdited = Object.keys(editedFields).length > 0;


    return (
        <React.Fragment>
            <TableCell align="center" sx={{
                padding: '8px',
                color: darkMode ? '#f5f7fa' : '#333',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontWeight: 600,
                fontSize: '0.875rem',
                position: 'relative',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                    borderLeft: `3px solid ${darkMode ? '#ffb74d' : '#f57c00'}`,
                }),
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
            // onClick={(e) => {
            //     e.stopPropagation()
            //     editMode && setEditingField({ id: row.id, field: 'fid' })
            // }}
            >
                {/* {editMode && editing.id === row.id && editing.field === 'fid' ?
                    <EditableNumberCell
                        id={row.id}
                        field={editing.field}
                        label={'Номер'}
                        value={row.fid}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    editMode ? ( */}
                <Box
                    onClick={(e) => {
                        e.stopPropagation()
                    }}
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}
                >
                    {/* <Checkbox
                                checked={selectedRows.has(row.id)}
                                onChange={(e) => {
                                    e.stopPropagation()
                                    handleRowSelection(row.id, e.target.checked)
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                                size="small"
                                sx={{
                                    p: '2px',
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                }}
                            /> */}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.accounting.id}
                    </Typography>
                </Box>
                {/* ) : (
                        row.fid
                    )
                } */}
            </TableCell>
            <TableCell sx={{
                padding: '8px',
                color: darkMode ? '#f5f7fa' : '#333',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontWeight: 500,
                fontSize: '0.875rem',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                })
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'start', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            openObjectView(row);
                        }}
                        sx={{
                            padding: '2px',
                            color: '#2196F3',
                            '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                color: '#1976D2'
                            }
                        }}
                        title="Открыть обзор опоры в новой вкладке"
                    >
                        {/* <LocationOnIcon sx={{ fontSize: { xs: '12px', md: '16px' } }} /> */}
                        <Box sx={{ position: 'relative', width: 28, height: 28 }}>
                            <Box style={{ position: 'absolute', margin: '4px 0 0 8px' }}>
                                {pillarIcon(row.type, row.status, row.material_type, row.supports_count, 12)}
                            </Box>
                            {[2, 3, 4, 7].includes(Number(row?.layer)) ? (
                                <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                    {projectStatusOreolIcon('pillar', Number(row?.layer), 28)}
                                </Box>
                            ) : null}
                        </Box>
                    </IconButton>
                    <Typography sx={{ fontSize: '0.875rem', flex: 1 }}>
                        {row.name}
                    </Typography>
                    {/* {editMode && (
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleOpenEditDialog(row);
                            }}
                            sx={{
                                padding: '2px',
                                color: '#4caf50',
                                '&:hover': {
                                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                    color: '#388e3c'
                                }
                            }}
                            title="Редактировать объект"
                        >
                            <EditIcon sx={{ fontSize: { xs: '12px', md: '16px' } }} />
                        </IconButton>
                    )}
                    {isRowEdited && (
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: darkMode ? '#ffb74d' : '#f57c00',
                                animation: 'pulse 2s infinite',
                                '@keyframes pulse': {
                                    '0%': { opacity: 1 },
                                    '50%': { opacity: 0.5 },
                                    '100%': { opacity: 1 },
                                }
                            }}
                        />
                    )} */}
                </Box>
            </TableCell>

            {/* Колонка: Выявленные отступления от ПСД */}
            <TableCell sx={{
                height: '1px',
                padding: '8px',
                color: darkMode ? '#f5f7fa' : '#333',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontSize: '0.8rem',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                })

            }}>

                <Box sx={{ display: 'grid', gridAutoRows: '1fr', flex: 1, alignItems: 'center', gap: 0.3, height: '100%' }}>
                    {row.accounting.checklist && row.accounting.checklist.length > 0 ? (
                        row.accounting.checklist.map((checklistItem, index) => {
                            const rule = rulesMap[checklistItem];
                            if (!rule) return null;

                            const part_elimin_categ = editedFields?.part_elimin_categ || row.accounting.part_elimin_categ || { ready: [], success: [], rejected: [] };
                            // const isReadyChecked = part_elimin_categ.ready?.includes(checklistItem) || false;
                            // const isRejected = part_elimin_categ.rejected?.includes(checklistItem) || false;
                            // const isSuccessChecked = part_elimin_categ.success?.includes(checklistItem) || false;

                            const statusLog = editedFields?.status_log?.[checklistItem] || row.accounting.status_log?.[checklistItem];
                            const isReadyChecked = statusLog?.ready || part_elimin_categ.ready?.includes(checklistItem) || false;
                            const isRejected = statusLog?.rejected || part_elimin_categ.rejected?.includes(checklistItem) || false;
                            const isSuccessChecked = statusLog?.success || part_elimin_categ.success?.includes(checklistItem) || false;
                            const _statusLog = row.accounting._status_log?.[checklistItem];

                            return (
                                <Box key={checklistItem} sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    height: '100%',
                                    alignContent: 'center',
                                    borderTop: index > 0 ?
                                        (darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)') : 'none',
                                    alignItems: 'flex-start',
                                    gap: 0
                                }}>
                                    {(statusLog?.ready || statusLog?.rejected) && _statusLog &&
                                        <Typography
                                            sx={{
                                                fontSize: '0.6rem',
                                                color: statusLog?.rejected ? '#9C27B0' : '#2196F3'
                                            }}
                                        >
                                            {_statusLog?.date} {_statusLog?.user?.short_fio} ({_statusLog?.user?.post_for_select})
                                        </Typography>
                                    }
                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
                                        <Checkbox
                                            checked={isReadyChecked}
                                            indeterminate={isRejected}
                                            disabled={!canEditReady || isSuccessChecked}
                                            onChange={(e) => {

                                                let partEliminCateg = {
                                                    ready: Array.isArray(part_elimin_categ.ready) ? part_elimin_categ.ready : [],
                                                    rejected: Array.isArray(part_elimin_categ.rejected) ? part_elimin_categ.rejected : [],
                                                    success: Array.isArray(part_elimin_categ.success) ? part_elimin_categ.success : [],
                                                };
                                                if (isRejected) {
                                                    partEliminCateg.ready = partEliminCateg.ready?.filter(v => v !== checklistItem) || [];
                                                    partEliminCateg.rejected = partEliminCateg.rejected?.filter(v => v !== checklistItem) || [];
                                                } else if (isReadyChecked) {
                                                    partEliminCateg.ready = partEliminCateg.ready?.filter(v => v !== checklistItem) || [];
                                                    partEliminCateg.rejected = [...(partEliminCateg.rejected || []), checklistItem];
                                                } else {
                                                    partEliminCateg.ready = [...(partEliminCateg.ready || []), checklistItem];
                                                    partEliminCateg.rejected = partEliminCateg.rejected?.filter(v => v !== checklistItem) || [];
                                                }

                                                saveEditedField({
                                                    rowId: row.accounting.id,
                                                    field: 'part_elimin_categ',
                                                    value: partEliminCateg,
                                                    isValueChanged:
                                                        (row.accounting?.part_elimin_categ?.ready || []).length !== partEliminCateg.ready?.length ||
                                                        (row.accounting?.part_elimin_categ?.rejected || []).length !== partEliminCateg.rejected?.length ||
                                                        (row.accounting?.part_elimin_categ?.success || []).length !== partEliminCateg.success?.length ||
                                                        (row.accounting?.part_elimin_categ?.ready || []).some(v => !partEliminCateg.ready.includes(v)) ||
                                                        (row.accounting?.part_elimin_categ?.rejected || []).some(v => !partEliminCateg.rejected.includes(v)) ||
                                                        (row.accounting?.part_elimin_categ?.success || []).some(v => !partEliminCateg.success.includes(v))
                                                })
                                                let statusLog = JSON.parse(JSON.stringify(row.accounting.status_log)) || {};

                                                if (isRejected) {
                                                    statusLog[checklistItem] = {
                                                        ...statusLog[checklistItem] || {},
                                                        ready: false,
                                                        rejected: false
                                                    }
                                                } else if (isReadyChecked) {
                                                    statusLog[checklistItem] = {
                                                        ...statusLog[checklistItem] || {},
                                                        ready: false,
                                                        rejected: true
                                                    }
                                                } else {
                                                    statusLog[checklistItem] = {
                                                        ...statusLog[checklistItem] || {},
                                                        ready: true,
                                                        rejected: false
                                                    }
                                                }

                                                saveEditedField({
                                                    rowId: row.accounting.id,
                                                    field: 'status_log',
                                                    value: statusLog,
                                                    isValueChanged:
                                                        (row.accounting?.status_log?.[checklistItem]?.ready || row.accounting?.part_elimin_categ?.ready?.includes(checklistItem) || false) !== (statusLog[checklistItem]?.ready || false) ||
                                                        (row.accounting?.status_log?.[checklistItem]?.rejected || row.accounting?.part_elimin_categ?.rejected?.includes(checklistItem) || false) !== (statusLog[checklistItem]?.rejected || false) ||
                                                        (row.accounting?.status_log?.[checklistItem]?.success || row.accounting?.part_elimin_categ?.success?.includes(checklistItem) || false) !== (statusLog[checklistItem]?.success || false),
                                                    checklistItem,
                                                    rejected: isReadyChecked


                                                })

                                                let newStatus = row.accounting.status
                                                if (Object.values(partEliminCateg).every(p => p.length === 0)) {
                                                    newStatus = 'Не устранено'
                                                } else if (partEliminCateg.success?.length === row.accounting.checklist?.length) {
                                                    newStatus = 'Устранено'
                                                } else if (partEliminCateg.ready?.length === row.accounting.checklist?.length) {
                                                    newStatus = 'Готово к проверке'
                                                } else if (partEliminCateg.rejected?.length === row.accounting.checklist?.length) {
                                                    newStatus = 'Отклонено'
                                                } else {
                                                    newStatus = 'Частично устранено'
                                                }

                                                saveEditedField({
                                                    rowId: row.accounting.id,
                                                    field: 'status',
                                                    value: newStatus,
                                                    isValueChanged: newStatus !== row.accounting.status
                                                })



                                            }}
                                            size="small"
                                            indeterminateIcon={
                                                <Box sx={{ position: 'relative', width: '20px', height: '20px', padding: 0, margin: 0 }}>
                                                    {/* <CheckBoxOutlineBlankIcon sx={{ fontSize: "1.25rem" }} /> */}
                                                    <svg width={'20px'} height={'20px'} className="MuiSvgIcon-root MuiSvgIcon-fontSizeMedium" focusable="false" aria-hidden="true" viewBox="0 0 24 24" data-testid="CheckBoxOutlineBlankIcon">
                                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                                        <line x1="7" y1="7" x2="17" y2="17" stroke="white" strokeWidth="2" />
                                                        <line x1="17" y1="7" x2="7" y2="17" stroke="white" strokeWidth="2" />
                                                    </svg>
                                                    {/* <CloseIcon sx={{ fill: 'white !important', position: 'absolute', top: 0, left: 0, transformOrigin: 'left top', transform: 'scale(0.8)' }} /> */}
                                                </Box>
                                            }
                                            sx={{
                                                padding: 0,
                                                color: isRejected ? '#9C27B0' : '#2196F3',
                                                '& .MuiSvgIcon-root': { fill: isRejected ? '#9C27B0' : '#2196F3' },
                                                '&.Mui-disabled': { opacity: 0.5 },
                                                "&.Mui-indeterminate": {
                                                    color: "#9C27B0",
                                                },
                                                "&.Mui-checked": {
                                                    color: "#2196F3",
                                                },
                                                '&.Mui-disabled': {
                                                    opacity: 0.5
                                                },
                                            }}
                                        />
                                        <Checkbox
                                            checked={isSuccessChecked}
                                            disabled={!canEditSuccess}
                                            onChange={(e) => {
                                                let partEliminCateg = {
                                                    ready: Array.isArray(part_elimin_categ.ready) ? part_elimin_categ.ready : [],
                                                    rejected: Array.isArray(part_elimin_categ.rejected) ? part_elimin_categ.rejected : [],
                                                    success: Array.isArray(part_elimin_categ.success) ? part_elimin_categ.success : [],
                                                };
                                                if (!e.target.checked) {
                                                    partEliminCateg.success = partEliminCateg.success.filter(v => v !== checklistItem);
                                                } else {
                                                    partEliminCateg.success = [...partEliminCateg.success, checklistItem];
                                                }
                                                saveEditedField({
                                                    rowId: row.accounting.id,
                                                    field: 'part_elimin_categ',
                                                    value: partEliminCateg,
                                                    isValueChanged:
                                                        (row.accounting?.part_elimin_categ?.ready || []).length !== partEliminCateg.ready?.length ||
                                                        (row.accounting?.part_elimin_categ?.rejected || []).length !== partEliminCateg.rejected?.length ||
                                                        (row.accounting?.part_elimin_categ?.success || []).length !== partEliminCateg.success?.length ||
                                                        (row.accounting?.part_elimin_categ?.ready || []).some(v => !partEliminCateg.ready.includes(v)) ||
                                                        (row.accounting?.part_elimin_categ?.rejected || []).some(v => !partEliminCateg.rejected.includes(v)) ||
                                                        (row.accounting?.part_elimin_categ?.success || []).some(v => !partEliminCateg.success.includes(v))
                                                })

                                                let statusLog = JSON.parse(JSON.stringify(row.accounting.status_log)) || {};
                                                if (e.target.checked) {
                                                    statusLog[checklistItem] = {
                                                        ...statusLog[checklistItem] || {},
                                                        success: true
                                                    }
                                                } else {
                                                    statusLog[checklistItem] = {
                                                        ...statusLog[checklistItem] || {},
                                                        success: false
                                                    }
                                                }
                                                saveEditedField({
                                                    rowId: row.accounting.id,
                                                    field: 'status_log',
                                                    value: statusLog,
                                                    isValueChanged:
                                                        (row.accounting?.status_log?.[checklistItem]?.ready || row.accounting?.part_elimin_categ?.ready?.includes(checklistItem) || false) !== (statusLog[checklistItem]?.ready || false) ||
                                                        (row.accounting?.status_log?.[checklistItem]?.rejected || row.accounting?.part_elimin_categ?.rejected?.includes(checklistItem) || false) !== (statusLog[checklistItem]?.rejected || false) ||
                                                        (row.accounting?.status_log?.[checklistItem]?.success || row.accounting?.part_elimin_categ?.success?.includes(checklistItem) || false) !== (statusLog[checklistItem]?.success || false)

                                                })

                                                let newStatus = row.accounting.status
                                                if (Object.values(partEliminCateg).every(p => p.length === 0)) {
                                                    newStatus = 'Не устранено'
                                                } else if (partEliminCateg.success?.length === row.accounting.checklist?.length) {
                                                    newStatus = 'Устранено'
                                                } else if (partEliminCateg.ready?.length === row.accounting.checklist?.length) {
                                                    newStatus = 'Готово к проверке'
                                                } else if (partEliminCateg.rejected?.length === row.accounting.checklist?.length) {
                                                    newStatus = 'Отклонено'
                                                } else {
                                                    newStatus = 'Частично устранено'
                                                }

                                                saveEditedField({
                                                    rowId: row.accounting.id,
                                                    field: 'status',
                                                    value: newStatus,
                                                    isValueChanged: newStatus !== row.accounting.status
                                                })
                                            }}
                                            size="small"
                                            sx={{
                                                padding: 0,
                                                color: `#48B349`,
                                                '& .MuiSvgIcon-root': { fill: `#48B349` },
                                                '&.Mui-disabled': { opacity: 0.5 }
                                            }}
                                        />
                                        <Typography sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' }, fontWeight: 'bold', color: `rgb(${codeColors[checklistItem[0]] || [0, 0, 0]})` }}>
                                            {rule.code}
                                        </Typography>
                                        <Typography sx={{
                                            marginLeft: '5px',
                                            fontSize: { xs: '0.7rem', md: '0.8rem' },
                                            fontWeight: isSuccessChecked || isReadyChecked || isRejected ? 'bold' : 'normal',
                                            color: isSuccessChecked ? `#48B349` : isReadyChecked ? `#2196F3` : isRejected ? '#9C27B0' : "inherit"
                                        }}>
                                            {rule.name}
                                        </Typography>
                                    </Box>
                                    {statusLog?.success && _statusLog &&
                                        <Typography
                                            sx={{
                                                fontSize: '0.6rem',
                                                color: '#48B349'
                                            }}
                                        >
                                            {_statusLog?.date} {_statusLog?.user?.short_fio} ({_statusLog?.user?.post_for_select})
                                        </Typography>
                                    }
                                </Box>
                            );
                        }).filter(Boolean)
                    ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: darkMode ? '#bbb' : '#666' }}>
                            Нет данных
                        </Typography>
                    )}
                </Box>
                {/* </Box> */}
            </TableCell>

            {/* Колонка: Указания и сроки их выполнения */}
            <TableCell sx={{
                height: '1px',
                padding: '8px',
                color: darkMode ? '#f5f7fa' : '#333',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontSize: '0.8rem',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                })
            }}>
                <Box sx={{ display: 'grid', gridAutoRows: '1fr', flex: 1, alignItems: 'center', gap: 0.3, height: '100%' }}>
                    {/* <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}> */}
                    {row.accounting.checklist && row.accounting.checklist.length > 0 ? (
                        row.accounting.checklist.map((checklistItem, index) => {
                            const rule = rulesMap[checklistItem];
                            if (!rule) return null;

                            const part_elimin_categ = row.accounting.part_elimin_categ || { ready: [], success: [], rejected: [] };
                            const isRejected = part_elimin_categ.rejected?.includes(checklistItem) || false;
                            const statusLog = row.accounting.status_log?.[checklistItem];

                            return (
                                <Box key={checklistItem} sx={{
                                    height: '100%',
                                    alignContent: 'center',
                                    borderTop: index > 0 ?
                                        (darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)') : 'none',
                                }}>
                                    <Typography sx={{ fontSize: '0.75rem' }}>
                                        {rule.description || 'Описание отсутствует'}
                                    </Typography>
                                    {isRejected && statusLog?.reason && (
                                        <Typography sx={{ fontSize: '0.7rem', color: '#9C27B0', mt: 0.5 }}>
                                            Причина: {statusLog.reason}
                                        </Typography>
                                    )}
                                </Box>

                            );
                        }).filter(Boolean)
                    ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: darkMode ? '#bbb' : '#666' }}>
                            Нет данных
                        </Typography>
                    )}
                </Box>
            </TableCell>

            {/* Колонка: Стоимость */}
            <TableCell align="center" sx={{
                height: '1px',
                padding: '8px',
                color: darkMode ? '#f5f7fa' : '#333',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontSize: '0.8rem',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                })
            }}>
                <Box sx={{ display: 'grid', gridAutoRows: '1fr', flex: 1, alignItems: 'center', gap: 0.3, height: '100%' }}>
                    {row.accounting.checklist && row.accounting.checklist.length > 0 ? (
                        row.accounting.checklist.map((checklistItem, index) => {
                            const rule = rulesMap[checklistItem];
                            if (!rule) return null;

                            return (
                                <Box key={checklistItem} sx={{
                                    height: '100%',
                                    alignContent: 'center',
                                    borderTop: index > 0 ?
                                        (darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)') : 'none',
                                }}>
                                    <Typography key={checklistItem} sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        color: darkMode ? '#4caf50' : '#2e7d32'
                                    }}>
                                        {new Intl.NumberFormat('ru-RU', {
                                            style: 'currency',
                                            currency: 'RUB',
                                        }).format(rule.cost || 0)}
                                    </Typography>
                                </Box>
                            );
                        }).filter(Boolean)
                    ) : (
                        <Typography sx={{ fontSize: '0.75rem', color: darkMode ? '#bbb' : '#666' }}>
                            -
                        </Typography>
                    )}
                </Box>
            </TableCell>



            <TableCell align="center" sx={{
                padding: '8px',
                color: darkMode ? '#bbb' : '#666',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontSize: '0.8rem',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                })
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                    {/* <Tooltip title="История изменений">
                        <IconButton
                            onClick={() => openHistoryDialog(row)}
                            size="small"
                            sx={{
                                color: darkMode ? '#ff9800' : '#f57c00',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip> */}

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, color: color }}>
                        {getStatusIcon(icon)}
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
                            {status}
                        </Typography>
                    </Box>
                    <Typography variant="caption" sx={{
                        color: darkMode ? '#bbb' : '#666',
                        fontSize: '0.75rem',
                    }}>
                        {row.accounting.status_date}
                    </Typography>

                </Box>
            </TableCell>

            <TableCell align="center" sx={{
                padding: '8px',
                color: darkMode ? '#bbb' : '#666',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                fontSize: '0.8rem',
                ...rowBackgroundColor,
                ...(isRowEdited && {
                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(255, 152, 0, 0.05)',
                })
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" sx={{
                        color: darkMode ? '#f5f7fa' : '#333',
                        fontWeight: 500,
                        fontSize: '0.75rem'
                    }}>
                        {/* {String(row.creator?.name || '')} */}
                        {row.accounting.creator.short_fio} ({row.accounting.creator.post_for_select})
                    </Typography>
                    <Typography variant="caption" sx={{
                        color: darkMode ? '#bbb' : '#666',
                        fontSize: '0.75rem',
                    }}>
                        {/* {formatDate(row.created_at)} */}
                        {row.accounting.created}
                    </Typography>
                </Box>
            </TableCell>
            {/* <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                // onClick={(e) => {
                //     e.stopPropagation()
                //     editMode && setEditingField({ id: row.id, field: 'plan_start' })
                // }}
            >
                {editMode && editing.id === row.id && editing.field === 'plan_start' ?
                    <EditableDateCell
                        id={row.id}
                        field={editing.field}
                        label={'Плановое начало'}
                        value={row.plan_start}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Typography variant="body2" sx={{
                        color: darkMode ? '#64b5f6' : '#1976d2',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                    }}
                    >
                        {formatDate(row.plan_start, true)}
                    </Typography>
                }
            </TableCell>


            <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'fact_start' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'fact_start' ?
                    <EditableDateCell
                        id={row.id}
                        field={editing.field}
                        label={'Фактическое начало'}
                        value={row.fact_start}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Typography variant="body2" sx={{
                        color: row.fact_start
                            ? (darkMode ? '#4caf50' : '#2e7d32')
                            : (darkMode ? '#999' : '#666'),
                        fontWeight: row.fact_start ? 500 : 400,
                        fontSize: '0.8rem',
                        fontStyle: row.fact_start ? 'normal' : 'italic',
                    }}

                    >
                        {formatDate(row.fact_start, true)}
                    </Typography>
                }

            </TableCell>


            <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.15)' : 'rgba(25, 118, 210, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'plan_end' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'plan_end' ?
                    <EditableDateCell
                        id={row.id}
                        field={editing.field}
                        label={'Плановое завершение'}
                        value={row.plan_end}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Typography variant="body2" sx={{
                        color: darkMode ? '#64b5f6' : '#1976d2',
                        fontWeight: 500,
                        fontSize: '0.8rem',
                    }}>
                        {formatDate(row.plan_end, true)}
                    </Typography>

                }


            </TableCell>


            <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(46, 125, 50, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'fact_end' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'fact_end' ?
                    <EditableDateCell
                        id={row.id}
                        field={editing.field}
                        label={'Фактическое завершение'}
                        value={row.fact_end}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Typography variant="body2" sx={{
                        color: row.fact_end
                            ? (darkMode ? '#4caf50' : '#2e7d32')
                            : (darkMode ? '#999' : '#666'),
                        fontWeight: row.fact_end ? 500 : 400,
                        fontSize: '0.8rem',
                        fontStyle: row.fact_end ? 'normal' : 'italic',
                    }}>
                        {formatDate(row.fact_end, true)}
                    </Typography>

                }


            </TableCell>



            <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(171, 71, 188, 0.15)' : 'rgba(106, 27, 154, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'stage' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'stage' ?
                    <EditableNumberCell
                        id={row.id}
                        field={editing.field}
                        label={'Номер стадии'}
                        value={row.stage}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                    />
                    :
                    <Chip
                        label={String(row.stage || '')}
                        size="small"
                        onClick={() => { }}
                        sx={{
                            bgcolor: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                            color: darkMode ? '#ab47bc' : '#6a1b9a',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            border: `1px solid ${darkMode ? '#ab47bc' : '#6a1b9a'}30`,
                            height: 'auto',
                            minHeight: '24px',
                            maxWidth: '100%',
                            cursor: editMode ? 'pointer' : 'auto',
                            '&:hover': editMode ? {
                                bgcolor: darkMode ? 'rgba(171, 71, 188, 0.3)' : 'rgba(106, 27, 154, 0.2)',
                                transform: 'scale(1.02)',
                            } : {},
                            transition: 'all 0.2s ease',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: '1.2',
                                padding: '4px 8px',
                            },
                        }}
                    />
                }
            </TableCell>
            <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(211, 47, 47, 0.15)' : 'rgba(211, 47, 47, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'responsible' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'responsible' ?
                    <EditableSelectCell
                        id={row.id}
                        field={editing.field}
                        label={'Выберите ответственного'}
                        value={row.responsible}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                        options={responsible}
                        multiple={false}
                    />
                    :
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Chip
                            label={String(row.responsible?.name || '')}
                            size="small"
                            onClick={() => { }}
                            sx={{
                                bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                                color: darkMode ? '#f44336' : '#d32f2f',
                                fontSize: '0.7rem',
                                height: 'auto',
                                minHeight: '24px',
                                maxWidth: '100%',
                                cursor: editMode ? 'pointer' : 'auto',
                                '&:hover': editMode ? {
                                    bgcolor: darkMode ? 'rgba(211, 47, 47, 0.3)' : 'rgba(211, 47, 47, 0.2)',
                                    transform: 'scale(1.02)',
                                } : {},
                                transition: 'all 0.2s ease',
                                '& .MuiChip-label': {
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    textAlign: 'center',
                                    lineHeight: '1.2',
                                    padding: '4px 8px',
                                },
                            }}
                        />
                    </Box>
                }
            </TableCell>
            <TableCell align="center" sx={{
                padding: '8px',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
                ...(editMode && {
                    '&:hover': {
                        bgcolor: darkMode ? 'rgba(245, 124, 0, 0.15)' : 'rgba(245, 124, 0, 0.1)',
                        cursor: 'pointer'
                    },
                })
            }}
                onClick={(e) => {
                    e.stopPropagation()
                    editMode && setEditingField({ id: row.id, field: 'coexecutors' })
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'coexecutors' ?
                    <EditableSelectCell
                        id={row.id}
                        field={editing.field}
                        label={'Выберите соисполнителей'}
                        value={row.coexecutors}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                        options={responsible}
                        multiple={true}
                    />
                    :
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        {(row.coexecutors || []).map((person, index) => (
                            <Chip
                                key={index}
                                label={String(person?.name || '')}
                                size="small"
                                onClick={() => { }}
                                sx={{
                                    bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                    color: darkMode ? '#ffb74d' : '#f57c00',
                                    fontSize: '0.7rem',
                                    height: 'auto',
                                    minHeight: '24px',
                                    maxWidth: '100%',
                                    cursor: editMode ? 'pointer' : 'auto',
                                    '&:hover': editMode ? {
                                        bgcolor: darkMode ? 'rgba(245, 124, 0, 0.3)' : 'rgba(245, 124, 0, 0.2)',
                                        transform: 'scale(1.02)',
                                    } : {},
                                    transition: 'all 0.2s ease',
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        textAlign: 'center',
                                        lineHeight: '1.2',
                                        padding: '4px 8px',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                }
            </TableCell>
            <TableCell
                align="center"

                sx={{
                    padding: '8px',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.08)'
                        : '1px solid rgba(0, 0, 0, 0.08)',
                    borderRight: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.05)'
                        : '1px solid rgba(0, 0, 0, 0.05)',
                    ...(editMode && {
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(92, 107, 192, 0.15)' : 'rgba(57, 73, 171, 0.1)',
                            cursor: 'pointer'
                        },
                    })
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    editMode && setEditingField({ id: row.id, field: 'equipment' });
                }}
            >
                {editMode && editing.id === row.id && editing.field === 'equipment' ? (
                    <EditableSelectCell
                        id={row.id}
                        field={editing.field}
                        label={'Выберите оснащение'}
                        value={row.equipment}
                        saveEditedField={saveEditedField}
                        darkMode={darkMode}
                        options={equipment}
                        multiple={true}
                    />
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        {(row.equipment || []).map((item, index) => (
                            <Chip
                                key={index}
                                label={String(item?.name || '')}
                                size="small"
                                onClick={() => { }}
                                sx={{
                                    bgcolor: darkMode ? 'rgba(92, 107, 192, 0.2)' : 'rgba(57, 73, 171, 0.1)',
                                    color: darkMode ? '#5c6bc0' : '#3949ab',
                                    fontSize: '0.7rem',
                                    height: 'auto',
                                    minHeight: '24px',
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word',
                                        textAlign: 'center',
                                        lineHeight: '1.2',
                                        padding: '4px 8px',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}
            </TableCell>
            
             */}
            {/* <TableCell align="center" sx={{
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.08)'
                    : '1px solid rgba(0, 0, 0, 0.08)',
                borderRight: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.05)'
                    : '1px solid rgba(0, 0, 0, 0.05)',
            }}>
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Редактировать">
                        <IconButton
                            onClick={() => openEditDialog(row)}
                            size="small"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="История изменений">
                        <IconButton
                            onClick={() => openHistoryDialog(row)}
                            size="small"
                            sx={{
                                color: darkMode ? '#ff9800' : '#f57c00',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <HistoryIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Удалить">
                        <IconButton
                            onClick={() => deleteObject(row)}
                            size="small"
                            sx={{
                                color: darkMode ? '#f44336' : '#d32f2f',
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </TableCell> */}
        </React.Fragment >
    );
}

// Утилиты для работы с localStorage
const STORAGE_KEYS = {
    ACCOUTINGS_SHEET_SETTINGS: 'accountings_sheet_settings'
};

const getStoredSettings = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.ACCOUTINGS_SHEET_SETTINGS);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const storeSettings = (settings) => {
    try {
        const current = getStoredSettings();
        const updated = { ...current, ...settings };
        localStorage.setItem(STORAGE_KEYS.ACCOUTINGS_SHEET_SETTINGS, JSON.stringify(updated));
    } catch (error) {
        console.warn('Failed to save objects reestr settings:', error);
    }
};

const AccountingSheetView = ({
    isModal = false,
    onClose = null,
    map = null,
    fromStartPage = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector(state => state.state.user);
    const darkMode = useSelector(state => state.state.darkMode);
    const dispatch = useDispatch()

    // Получаем сохраненные настройки
    const storedSettings = getStoredSettings();

    const data = useSelector(fromStartPage ? selectAccountingsGroupedByKtpNewWithoutAnyFilters : selectAccountingsGroupedByKtpNew);
    const rulesMap = useSelector(selectRulesMap);
    const checklistFilter = useSelector(selectChecklistFilter);
    const [filteredData, setFilteredData] = useState([]);
    const [groupedData, setGroupedData] = useState({ groupCounts: [], groupedData: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [users, setUsers] = useState([])
    const [accountingsProjects, setAccountingsProjects] = useState(null);

    // Состояние для меню фильтрации проектов
    const [projectsFilterAnchor, setProjectsFilterAnchor] = useState(null);
    const projectsFilterPopperRef = useRef(null);
    const [selectedObjects, setSelectedObjects] = useState(new Set(storedSettings.selectedObjects || []));
    const [treeSearchQuery, setTreeSearchQuery] = useState('');

    // const [objects, setObjects] = useState([]);

    const objects = useMemo(() => {
        if (!data) return []
        return data.map(d => ({
            id: d.tp.fid,
            name: `${d.ps.name} ${d.ps.operating_voltage} ${d.tp.name} ${d.tp.town}`
        }))
    }, [data])

    const [canEditReady, canEditSuccess] = useMemo(() => {
        return [
            user.roles.some(v => ['project_manager', 'smr_foreman'].includes(v)),
            user.roles.includes('authors_control')
        ];
    }, [user]);

    // Состояния для поиска и фильтрации
    const [searchQuery, setSearchQuery] = useState('');


    // Состояния для сортировки
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

    // Состояния для продвинутой фильтрации
    const [advancedFilters, setAdvancedFilters] = useState(storedSettings.advancedFilters ?? []);
    const [filterMode, setFilterMode] = useState(storedSettings.filterMode ?? 'objects'); // 'objects' | 'codes'
    const availableFilters = useMemo(() => ([
        { id: 'accounting_id', label: '№', type: 'number' },
        { id: 'checklist', label: 'Чек-лист', type: 'multi-select', options: Object.keys(rulesMap).map(code => ({ id: code, name: `${code} - ${rulesMap[code]?.name || 'Неизвестно'}`, label: code })) },
        { id: 'cost', label: 'Стоимость', type: 'number' },
        { id: 'status', label: 'Отметка о выполнении', type: 'multi-select', options: Object.entries(statuses).filter(([key, value]) => value.filter.length === 1).map(([key, value]) => ({ id: key, name: key, label: key })) },
        { id: 'status_date', label: 'Дата (отметка о выполнении)', type: 'date' },
        { id: 'status_ready', label: 'Статус (готово к проверке)', type: 'boolean' },
        { id: 'status_rejected', label: 'Статус (отклонено)', type: 'boolean' },
        { id: 'status_success', label: 'Статус (устранено)', type: 'boolean' },
        { id: 'status_ready_date', label: 'Дата (готово к проверке)', type: 'date' },
        { id: 'status_reject_date', label: 'Дата (отклонено)', type: 'date' },
        { id: 'status_success_date', label: 'Дата (устранено)', type: 'date' },
        { id: 'status_ready_user', label: 'Пользователь (готово к проверке)', type: 'multi-select', options: users.map(user => ({ id: user.id, name: user.name, label: user.name })) },
        { id: 'status_reject_user', label: 'Пользователь (отклонено)', type: 'multi-select', options: users.map(user => ({ id: user.id, name: user.name, label: user.name })) },
        { id: 'status_success_user', label: 'Пользователь (устранено)', type: 'multi-select', options: users.map(user => ({ id: user.id, name: user.name, label: user.name })) }
    ]), [rulesMap, statuses, users]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Состояние для меню экспорта
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

    // Состояние для выбранных строк
    const [selectedRows, setSelectedRows] = useState(new Set());


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await $authHost.get('/apig/users');
                const { success, data } = response.data;

                setUsers(success ? data.filter(d => d.vio_id).map(d => ({
                    id: d.vio_id || 0,
                    name: d.display_name
                })) : []);
            } catch (e) {
                setUsers([])
                console.log(e)
                setError(e.message);
            }
        };
        fetchUsers();
    }, []);

    useEffect(() => {
        const fetchAccountingsProjects = async () => {
            try {
                const response = await $authHost.get('/apig/state/accountings_projects');
                let { data } = response

                setAccountingsProjects(data);
            } catch (e) {
                setAccountingsProjects(null);
                console.log('Error fetching accountings_projects:', e);
            }
        };
        fetchAccountingsProjects();
    }, []);

    // Функция для фильтрации и сортировки данных
    const filterAndSortData = useCallback((rawData, search, objectsFilter, advancedFilters, sortBy, sortDirection, filterMode) => {
        if (!objectsFilter || objectsFilter.length === 0) return [];
        let filtered = [...rawData];

        // Поиск
        if (search.trim()) {
            // Разбиваем поисковый запрос на отдельные слова и фильтруем пустые
            const searchTerms = search.toLowerCase().split(/\s+/).filter(term => term.length > 0);

            filtered = filtered.map(item => {
                const pillars = item.pillars.filter(pill =>
                    searchTerms.every(word =>
                        `${pill.accounting.id} ${pill.name} ${pill.accounting.retreat_psd || ''} ${pill.accounting.instructions || ''} ${pill.accounting.checklist ? pill.accounting.checklist.join(' ') : ''} ${pill.accounting.status} ${pill.accounting.created} ${pill.accounting.creator}`.toLowerCase().includes(word))
                );
                // Подготавливаем все текстовые поля для поиска

                return {
                    ...item,
                    pillars
                }
            }).filter(item => item.pillars.length > 0);
        }

        // Фильтрация по проектам
        filtered = filtered.filter(item => {
            return [...objectsFilter].some(f => f.endsWith(item.tp.fid.toString()));
        });


        console.log(filtered)

        // Продвинутая фильтрация
        if (advancedFilters && advancedFilters.length > 0) {
            advancedFilters.forEach(filter => {
                console.log(filter)
                switch (filter.id) {
                    case 'accounting_id':
                        if (filter.value && filter.value !== '') {
                            const filterId = Number(filter.value);
                            if (!isNaN(filterId)) {
                                filtered = filtered.map(item => {
                                    return {
                                        ...item,
                                        pillars: item.pillars.filter(pillar => {
                                            const itemId = Number(pillar.accounting?.id);
                                            if (isNaN(itemId)) return false;

                                            switch (filter.operator) {
                                                case 'equals':
                                                    return itemId === filterId;
                                                case 'greater':
                                                    return itemId > filterId;
                                                case 'less':
                                                    return itemId < filterId;
                                                default:
                                                    return true;
                                            }
                                        })
                                    }

                                }).filter(item => item.pillars.length > 0);
                            }
                        }
                        break;

                    case 'checklist':
                        if (filter.value && filter.value.length > 0) {
                            const checklistCodes = filter.value.map(c => c.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars?.map(pillar => {
                                        if (!pillar.accounting?.checklist || pillar.accounting.checklist.length === 0) return null;

                                        if (filterMode === 'codes') {
                                            const checklist = pillar.accounting.checklist.filter(code => checklistCodes.includes(code))
                                            if (!checklist || checklist.length === 0) return null
                                            return {
                                                ...pillar,
                                                accounting: {
                                                    ...pillar.accounting,
                                                    checklist
                                                }
                                            }
                                        }

                                        if (filter.logic === 'and') {
                                            // И - все выбранные значения должны присутствовать
                                            if (checklistCodes.every(code => pillar.accounting.checklist.includes(code))) return pillar;
                                        } else {
                                            // ИЛИ - любое из выбранных значений
                                            if (pillar.accounting.checklist.some(code =>
                                                checklistCodes.includes(code)
                                            )) return pillar;
                                        }
                                    }).filter(f => f) || []
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        console.log(filtered)
                        break;
                    case 'checklist_codes':
                        if (filter.value && filter.value.length > 0) {
                            const checklistCodes = filter.value.map(c => c.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars?.map(pillar => {
                                        if (!pillar.accounting?.checklist || pillar.accounting.checklist.length === 0) return null;


                                    }).filter(f => f) || []
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        console.log(filtered)
                        break;
                    case 'cost':
                        if (filter.value && filter.value !== '') {
                            const filterCost = Number(filter.value);
                            if (!isNaN(filterCost)) {
                                filtered = filtered.map(item => {
                                    return {
                                        ...item,
                                        pillars: item.pillars.filter(pillar => {
                                            if (!pillar.accounting?.checklist || pillar.accounting.checklist.length === 0) return false;

                                            const totalCost = pillar.accounting.checklist.reduce((sum, code) => {
                                                return sum + (Number(rulesMap[code]?.cost) || 0);
                                            }, 0);

                                            switch (filter.operator) {
                                                case 'equals':
                                                    return totalCost === filterCost;
                                                case 'greater':
                                                    return totalCost > filterCost;
                                                case 'less':
                                                    return totalCost < filterCost;
                                                default:
                                                    return true;
                                            }
                                        })
                                    }
                                }).filter(item => item.pillars.length > 0);
                            }
                        }
                        break;

                    case 'status':
                        if (filter.value && filter.value.length > 0) {
                            const statusValues = filter.value.map(s => s.name);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status) return false;

                                        if (filter.logic === 'equals') {
                                            return statusValues.some(status =>
                                                pillar.accounting.status === status
                                            );
                                        } else {
                                            return statusValues.every(status =>
                                                pillar.accounting.status !== status
                                            );
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_date':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_date) return false;
                                        const itemDate = new Date(pillar.accounting.status_date);
                                        switch (filter.operator) {
                                            case 'equals':
                                                return itemDate.getDate() === filterDate.getDate();
                                            case 'greater':
                                                return itemDate > filterDate;
                                            case 'less':
                                                return itemDate < filterDate;
                                            case 'between':
                                                if (!filter.value2) return true;
                                                const filterDate2 = new Date(filter.value2);
                                                return itemDate >= filterDate && itemDate <= filterDate2;
                                            default:
                                                return true;
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_ready':
                        if (filter.value && filter.value !== '') {
                            const filterValue = filter.value === 'true';
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.map(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        if (filterMode === 'codes') {
                                            const codes = [...(pillar.accounting?.part_elimin_categ?.ready || []), ...Object.entries(pillar.accounting.status_log).filter(([code, log]) => log.ready).map(([code, log]) => code)]
                                            const checklist = filterValue ? pillar.accounting.checklist.filter(code => codes.includes(code)) : pillar.accounting.checklist.filter(code => !codes.includes(code))
                                            if (!checklist || checklist.length === 0) return null
                                            return {
                                                ...pillar,
                                                accounting: {
                                                    ...pillar.accounting,
                                                    checklist
                                                }
                                            }
                                        }

                                        const hasReady = Object.values(pillar.accounting.status_log).some(log => log.ready);
                                        if (hasReady !== filterValue) return null;
                                        return pillar
                                    }).filter(f => f)
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_rejected':
                        if (filter.value && filter.value !== '') {
                            const filterValue = filter.value === 'true';
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.map(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        if (filterMode === 'codes') {
                                            const codes = [...(pillar.accounting?.part_elimin_categ?.rejected || []), ...Object.entries(pillar.accounting.status_log).filter(([code, log]) => log.rejected).map(([code, log]) => code)]
                                            const checklist = filterValue ? pillar.accounting.checklist.filter(code => codes.includes(code)) : pillar.accounting.checklist.filter(code => !codes.includes(code))
                                            if (!checklist || checklist.length === 0) return null
                                            return {
                                                ...pillar,
                                                accounting: {
                                                    ...pillar.accounting,
                                                    checklist
                                                }
                                            }
                                        }

                                        const hasRejected = Object.values(pillar.accounting.status_log).some(log => log.rejected);

                                        if (hasRejected !== filterValue) return null;
                                        return pillar
                                    }).filter(f => f)
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_success':
                        if (filter.value && filter.value !== '') {
                            const filterValue = filter.value === 'true';
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.map(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        if (filterMode === 'codes') {
                                            const codes = [...(pillar.accounting?.part_elimin_categ?.success || []), ...Object.entries(pillar.accounting.status_log).filter(([code, log]) => log.success).map(([code, log]) => code)]
                                            const checklist = filterValue ? pillar.accounting.checklist.filter(code => codes.includes(code)) : pillar.accounting.checklist.filter(code => !codes.includes(code))
                                            if (!checklist || checklist.length === 0) return null
                                            return {
                                                ...pillar,
                                                accounting: {
                                                    ...pillar.accounting,
                                                    checklist
                                                }
                                            }
                                        }

                                        const hasSuccess = Object.values(pillar.accounting.status_log).some(log => log.success);

                                        if (hasSuccess !== filterValue) return null;
                                        return pillar
                                    }).filter(f => f)
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_ready_date':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        // Ищем записи с ready: true и проверяем дату
                                        const readyEntries = Object.entries(pillar.accounting.status_log)
                                            .filter(([_, log]) => log.ready)
                                            .map(([_, log]) => log.dt);

                                        if (readyEntries.length === 0) return false;

                                        const itemDate = new Date(readyEntries[0]); // Берем первую дату
                                        switch (filter.operator) {
                                            case 'equals':
                                                return itemDate.getDate() === filterDate.getDate();
                                            case 'greater':
                                                return itemDate > filterDate;
                                            case 'less':
                                                return itemDate < filterDate;
                                            case 'between':
                                                if (!filter.value2) return true;
                                                const filterDate2 = new Date(filter.value2);
                                                return itemDate >= filterDate && itemDate <= filterDate2;
                                            default:
                                                return true;
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_reject_date':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        // Ищем записи с rejected: true и проверяем дату
                                        const rejectedEntries = Object.entries(pillar.accounting.status_log)
                                            .filter(([_, log]) => log.rejected)
                                            .map(([_, log]) => log.dt);

                                        if (rejectedEntries.length === 0) return false;

                                        const itemDate = new Date(rejectedEntries[0]); // Берем первую дату
                                        switch (filter.operator) {
                                            case 'equals':
                                                return itemDate.getDate() === filterDate.getDate();
                                            case 'greater':
                                                return itemDate > filterDate;
                                            case 'less':
                                                return itemDate < filterDate;
                                            case 'between':
                                                if (!filter.value2) return true;
                                                const filterDate2 = new Date(filter.value2);
                                                return itemDate >= filterDate && itemDate <= filterDate2;
                                            default:
                                                return true;
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_success_date':
                        if (filter.value) {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        // Ищем записи с success: true и проверяем дату
                                        const successEntries = Object.entries(pillar.accounting.status_log)
                                            .filter(([_, log]) => log.success)
                                            .map(([_, log]) => log.dt);

                                        if (successEntries.length === 0) return false;

                                        const itemDate = new Date(successEntries[0]); // Берем первую дату
                                        switch (filter.operator) {
                                            case 'equals':
                                                return itemDate.getDate() === filterDate.getDate();
                                            case 'greater':
                                                return itemDate > filterDate;
                                            case 'less':
                                                return itemDate < filterDate;
                                            case 'between':
                                                if (!filter.value2) return true;
                                                const filterDate2 = new Date(filter.value2);
                                                return itemDate >= filterDate && itemDate <= filterDate2;
                                            default:
                                                return true;
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_ready_user':
                        if (filter.value && filter.value.length > 0) {
                            const userIds = filter.value.map(u => u.id);
                            console.log(userIds)
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        // Ищем записи с ready: true и проверяем пользователя
                                        const readyEntries = Object.entries(pillar.accounting.status_log)
                                            .filter(([_, log]) => log.ready && log.userid);

                                        if (readyEntries.length === 0) return false;
                                        console.log(readyEntries)
                                        if (filter.logic === 'or') {
                                            return readyEntries.some(([_, log]) =>
                                                userIds.includes(log.userid)
                                            );
                                        } else {
                                            return readyEntries.every(([_, log]) =>
                                                userIds.includes(log.userid)
                                            );
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_reject_user':
                        if (filter.value && filter.value.length > 0) {
                            const userIds = filter.value.map(u => u.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        // Ищем записи с rejected: true и проверяем пользователя
                                        const rejectedEntries = Object.entries(pillar.accounting.status_log)
                                            .filter(([_, log]) => log.rejected && log.userid);

                                        if (rejectedEntries.length === 0) return false;

                                        if (filter.logic === 'or') {
                                            return rejectedEntries.some(([_, log]) =>
                                                userIds.includes(log.userid)
                                            );
                                        } else {
                                            return rejectedEntries.every(([_, log]) =>
                                                userIds.includes(log.userid)
                                            );
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;

                    case 'status_success_user':
                        if (filter.value && filter.value.length > 0) {
                            const userIds = filter.value.map(u => u.id);
                            filtered = filtered.map(item => {
                                return {
                                    ...item,
                                    pillars: item.pillars.filter(pillar => {
                                        if (!pillar.accounting?.status_log) return false;

                                        // Ищем записи с success: true и проверяем пользователя
                                        const successEntries = Object.entries(pillar.accounting.status_log)
                                            .filter(([_, log]) => log.success && log.userid);

                                        if (successEntries.length === 0) return false;

                                        if (filter.logic === 'or') {
                                            return successEntries.some(([_, log]) =>
                                                userIds.includes(log.userid)
                                            );
                                        } else {
                                            return successEntries.every(([_, log]) =>
                                                userIds.includes(log.userid)
                                            );
                                        }
                                    })
                                }
                            }).filter(item => item.pillars.length > 0);
                        }
                        break;
                }
            });







        }

        // Сортировка данных
        if (sortBy) {
            filtered = filtered.map(item => {
                // Сортируем pillars внутри каждого item
                const sortedPillars = [...item.pillars].sort((a, b) => {
                    let aValue, bValue;

                    switch (sortBy) {
                        case 'id':
                            aValue = a.accounting?.id || 0;
                            bValue = b.accounting?.id || 0;
                            break;
                        case 'pillar':
                            aValue = a.name || '';
                            bValue = b.name || '';
                            break;
                        case 'violations':
                            aValue = a.accounting?.retreat_psd || '';
                            bValue = b.accounting?.retreat_psd || '';
                            break;
                        case 'instructions':
                            aValue = a.accounting?.instructions || '';
                            bValue = b.accounting?.instructions || '';
                            break;
                        case 'cost':
                            aValue = a.accounting?.checklist?.reduce((a, v) => a + (parseFloat(rulesMap[v]?.cost || 0)), 0) || 0
                            bValue = b.accounting?.checklist?.reduce((a, v) => a + (parseFloat(rulesMap[v]?.cost || 0)), 0) || 0
                            break;
                        case 'status':
                            aValue = a.accounting?.status || '';
                            bValue = b.accounting?.status || '';
                            break;
                        case 'created':
                            aValue = new Date(a.accounting?.created || 0);
                            bValue = new Date(b.accounting?.created || 0);
                            break;
                        default:
                            return 0;
                    }
                    // Сравнение значений
                    let comparison = 0;
                    if (typeof aValue === 'string' && typeof bValue === 'string') {
                        comparison = aValue.localeCompare(bValue);
                    } else if (aValue instanceof Date && bValue instanceof Date) {
                        comparison = aValue.getTime() - bValue.getTime();
                    } else {
                        comparison = aValue - bValue;
                    }

                    return sortDirection === 'desc' ? -comparison : comparison;
                });

                return {
                    ...item,
                    pillars: sortedPillars
                };
            });
        } else {
            // Сортировка по возрастанию fid по умолчанию
            filtered.sort((a, b) => (a.fid || 0) - (b.fid || 0));
        }

        return filtered;
    }, [user.id]);



    // Обновляем отфильтрованные данные при изменении исходных данных или параметров
    useEffect(() => {

        const filtered = filterAndSortData(data, searchQuery, [...selectedObjects], advancedFilters, sortBy, sortDirection, filterMode);
        setFilteredData(filtered);
    }, [data, searchQuery, selectedObjects, advancedFilters, sortBy, sortDirection, filterMode, filterAndSortData]);

    console.log(selectedObjects)

    const activeAccoutingsProjects = useMemo(() => {
        if (!accountingsProjects || !data) return {};
        const projects = {}
        Object.entries(accountingsProjects).forEach(([projectId, project]) => {
            Object.entries(project.stages).forEach(([stageId, stage]) => {
                Object.entries(stage.objects).forEach(([objectId, objectName]) => {
                    if (data.some(item => item.tp.projects.includes(Number(projectId)))) {
                        if (!projects[projectId]) {
                            projects[projectId] = {
                                label: project.label,
                                is_smr: project.is_smr,
                                stages: {}
                            };
                        }
                        if (!projects[projectId].stages[stageId]) {
                            projects[projectId].stages[stageId] = { label: 'Этап ' + stageId, objects: {} };
                        }
                        projects[projectId].stages[stageId].objects[objectId] = objectName;
                    }
                });
            });
        });
        return projects;
    }, [accountingsProjects, data]);

    // Функции для работы с фильтрацией проектов
    const clearAllFilters = () => {
        setSelectedObjects(new Set());
        storeSettings({ selectedObjects: [] });
    };

    const selectAllFilters = () => {
        if (!activeAccoutingsProjects) return;
        const allKeys = new Set();

        Object.entries(activeAccoutingsProjects).forEach(([projectId, project]) => {
            allKeys.add(projectId);
            Object.entries(project.stages).forEach(([stageId, stage]) => {
                allKeys.add(`${projectId}-${stageId}`);
                Object.keys(stage.objects).forEach(objectId => {
                    allKeys.add(`${projectId}-${stageId}-${objectId}`);
                });
            });
        });

        setSelectedObjects(allKeys);
        storeSettings({ selectedObjects: Array.from(allKeys) });
    };

    // Преобразуем данные accountings_projects в формат для SimpleTreeView
    const treeData = useMemo(() => {
        if (!activeAccoutingsProjects) return [];
        console.log(activeAccoutingsProjects)

        const filterTreeData = (data, searchQuery) => {
            if (!searchQuery) return data;

            return data.filter(item => {
                const labelMatches = item.label.toLowerCase().includes(searchQuery.toLowerCase());
                const childrenFiltered = item.children ? filterTreeData(item.children, searchQuery) : [];

                if (labelMatches || childrenFiltered.length > 0) {
                    return {
                        ...item,
                        children: childrenFiltered
                    };
                }
                return false;
            }).map(item => ({
                ...item,
                children: item.children ? filterTreeData(item.children, searchQuery) : undefined
            }));
        };

        const allData = Object.entries(activeAccoutingsProjects).map(([projectId, project]) => ({
            id: projectId,
            level: 'projects',
            label: project.label,
            children: [...Object.entries(project.stages).map(([stageId, stage]) => {
                if (stageId != 0) {
                    return {
                        id: `${projectId}-${stageId}`,
                        label: stage.label,
                        level: 'stages',
                        children: Object.entries(stage.objects).map(([objectId, objectName]) => ({
                            id: `${projectId}-${stageId}-${objectId}`,
                            label: objectName,
                            level: 'objects',
                        }))
                    }
                }
                return null
            }).filter(f => f), ...(Object.entries(project.stages[0]?.objects || {}) || []).map(([objectId, objectName]) => ({
                id: `${projectId}-0-${objectId}`,
                label: objectName,
                level: 'objects'
            }))]
        }));

        return filterTreeData(allData, treeSearchQuery);
    }, [activeAccoutingsProjects, treeSearchQuery]);

    // Функция для рендеринга дерева с чекбоксами
    const renderTreeItem = (item) => {
        const isSelected = [...selectedObjects].some(f => f.startsWith(`${item.id}`));
        const count = [...selectedObjects].filter(f => f.startsWith(`${item.id}-`))?.length
        const isIndeterminate = item.level === 'objects' ? false : (count > 0 && count < item.children.length)

        return (
            <TreeItem
                key={item.id}
                itemId={item.id}
                label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            size="small"
                            checked={isSelected}
                            indeterminate={isIndeterminate}
                            onChange={(e) => {
                                e.stopPropagation();
                                const newSelectedObjects = new Set(selectedObjects);

                                function deleteChildren(item) {
                                    item.children?.forEach(child => newSelectedObjects.delete(child.id))
                                    if (item.children?.length > 0) {
                                        item.children?.forEach(child => deleteChildren(child))
                                    }
                                }

                                function addChildren(item) {
                                    item.children?.forEach(child => newSelectedObjects.add(child.id))
                                    if (item.children?.length > 0) {
                                        item.children?.forEach(child => addChildren(child))
                                    }
                                }

                                console.log(item.level)
                                if (isSelected) {
                                    newSelectedObjects.delete(item.id);
                                    deleteChildren(item)
                                } else {
                                    newSelectedObjects.add(item.id);
                                    addChildren(item)
                                }
                                setSelectedObjects(newSelectedObjects);
                            }}
                            sx={{
                                p: 0.5,
                                color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                '&.Mui-checked': {
                                    color: darkMode ? '#64b5f6' : '#1976d2'
                                }
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.8rem',
                                color: darkMode ? '#f5f7fa' : '#333',
                                fontWeight: item.level === 'projects' ? 600 : 400
                            }}
                        >
                            {item.level === 'stages' ? `${item.label} (${item.children?.length || 0})` : item.label}
                        </Typography>
                    </Box>
                }
            >
                {item.children?.map(child => renderTreeItem(child))}
            </TreeItem>
        );
    };


    // Функция для подготовки данных для группировки
    const prepareGroupedData = useCallback((filteredData, selectedObjects) => {
        // Если не выбран ни один КДП, показываем все данные без группировки
        if (!selectedObjects || selectedObjects.length === 0) {
            return {
                groupCounts: [],
                groupedData: []
            };
        }
        // Группируем по КДП
        const groupedByObjects = {};

        filteredData.forEach(item => {
            const objectName = `${item.ps.name} ${item.ps.operating_voltage} ${item.tp.name} ${item.tp.town}`

            if (!groupedByObjects[objectName]) {
                groupedByObjects[objectName] = [];
            }
            groupedByObjects[objectName] = item.pillars;
        });

        // Сортируем группы по названию КДП
        const sortedGroups = Object.keys(groupedByObjects).sort();

        // Создаем плоский массив данных с информацией о группе
        const groupedData = [];
        const groupCounts = [];

        sortedGroups.forEach((groupName, groupIndex) => {
            const groupItems = groupedByObjects[groupName];
            groupCounts.push(groupItems.length);

            groupItems.forEach(item => {
                groupedData.push({
                    ...item,
                    group: groupIndex,
                    groupName: groupName
                });
            });
        });

        // Добавляем пустые элементы в конец для принудительного рендеринга
        const paddingItems = Array.from({ length: sortedGroups.length }, (_, index) => ({
            id: `padding-${index}`,
            fid: groupedData.length + index + 1,
            group: groupedData.length > 0 ? groupedData[groupedData.length - 1].group : 0,
            groupName: groupedData.length > 0 ? groupedData[groupedData.length - 1].groupName : 'Все объекты',
            isPadding: true
        }));

        return { groupCounts, groupedData: [...groupedData, ...paddingItems] };
    }, []);

    // Обновляем группированные данные при изменении отфильтрованных данных
    useEffect(() => {
        const grouped = prepareGroupedData(filteredData, selectedObjects);

        setGroupedData(grouped);
    }, [filteredData, selectedObjects, prepareGroupedData]);

    // Закрываем меню при клике вне его
    useEffect(() => {
        if (!projectsFilterAnchor) return;

        const handleClickOutside = (event) => {
            // Проверяем, был ли клик внутри TextField (anchor) или внутри Popper (Paper)
            const clickedInsideAnchor = projectsFilterAnchor && projectsFilterAnchor.contains(event.target);
            const clickedInsidePopper = projectsFilterPopperRef.current && projectsFilterPopperRef.current.contains(event.target);
            
            // Если клик был вне обоих элементов, закрываем меню
            if (!clickedInsideAnchor && !clickedInsidePopper) {
                setProjectsFilterAnchor(null);
            }
        };

        // Используем небольшую задержку, чтобы клик на TextField не закрывал меню сразу
        const timeoutId = setTimeout(() => {
            document.addEventListener('mousedown', handleClickOutside);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [projectsFilterAnchor]);

    const [stages, setStages] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
    const [editMode, setEditMode] = useState(false)
    const [editing, setEditing] = useState({ id: null, field: null })
    const [editedRows, setEditedRows] = useState([])
    const [hasRejectedRows, setHasRejectedRows] = useState([])
    const [rejectionReason, setRejectionReason] = useState('');

    // Состояние для управления видимостью столбцов
    const [visibleColumns, setVisibleColumns] = useState(
        storedSettings.visibleColumns ?? columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {})
    );
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [columnWidths, setColumnWidths] = useState({});

    // Временные данные для фильтров (можно заменить на реальные данные из Redux)
    const [responsible, setResponsible] = useState([]);
    const [equipment, setEquipment] = useState([]);


    // Сохранение настроек в localStorage при изменении
    useEffect(() => {
        storeSettings({ darkMode });
    }, [darkMode]);

    useEffect(() => {
        storeSettings({ selectedObjects: Array.from(selectedObjects) });
    }, [selectedObjects]);

    useEffect(() => {
        storeSettings({ visibleColumns });
    }, [visibleColumns]);

    useEffect(() => {
        if (user.roles.includes('viewer')) {
            setVisibleColumns(prev => ({
                ...prev,
                'cost': false
            }));
        }
    }, [user])

    useEffect(() => {
        if (!fromStartPage && checklistFilter) {
            const newFilters = advancedFilters.filter(f => f.id !== 'checklist');

            const filter = {
                id: 'checklist',
                label: "Чек-лист",
                type: 'multi-select',
                operator: null,
                value: checklistFilter.map(code => ({ id: code, label: code, name: '' })),
                value2: null,
                logic: 'or',
                enabled: true
            };
            newFilters.push(filter);
            setFilterMode('codes')


            setAdvancedFilters(newFilters);
        }
    }, [fromStartPage, checklistFilter])



    // useEffect(() => {
    //     console.log(hasRejectedRows)
    // }, [hasRejectedRows]);

    const handleEditedField = ({ rowId, field, value, isValueChanged, checklistItem, rejected }) => {
        // console.log(rowId, field, value, isValueChanged, checklistItem, rejected)
        if (isValueChanged) {
            // Записываем изменение в editedRows
            setEditedRows(prev => {
                const existingRow = prev.find(row => row.id === rowId);
                if (existingRow) {
                    // Если строка уже есть, обновляем поле
                    return prev.map(row =>
                        row.id === rowId
                            ? { ...row, changes: { ...row.changes, [field]: value } }
                            : row
                    );
                } else {
                    // Если строки нет, добавляем новую
                    return [...prev, {
                        id: rowId,
                        changes: { [field]: value }
                    }];
                }
            });




        } else {
            setEditedRows(prev => {
                const existingRow = prev.find(row => row.id === rowId);
                if (existingRow) {
                    const changes = existingRow.changes;
                    delete changes[field];
                    if (Object.keys(changes).length === 0) {
                        return prev.filter(row => row.id !== rowId);
                    }
                    return prev.map(row => row.id === rowId ? { ...row, changes } : row);
                }
                return prev;
            });
        }

        if (checklistItem) {
            if (rejected) {
                setHasRejectedRows(prev => ([...prev, `${rowId}_${checklistItem}`]))
            } else {
                setHasRejectedRows(prev => prev.filter(p => p !== `${rowId}_${checklistItem}`))
            }
        }

        setEditing({ id: null, field: null })
    }

    const handleChangeEdititng = ({ id, field }) => {
        setEditing({ id, field })
    }







    const handleDeleteObject = async (object) => {
        if (window.confirm(`Вы уверены, что хотите удалить объект "${object.id}"?`)) {
            try {
                const response = await $authHost.delete(`/apig/reestr/${object.id}`, {
                    data: {
                        modifier: user.id
                    }
                });
                const { success } = response.data;

                if (success) {
                    // Обновить данные - убираем удаленный объект из состояния
                    // setData(prevData => prevData.filter(item => item.id !== object.id));
                    // alert('Объект успешно удален');
                } else {
                    throw new Error(response.error || 'Неизвестная ошибка');
                }
            } catch (error) {
                console.error('Error deleting object:', error);
                alert(`Ошибка при удалении: ${error.message}`);
            }
        }
    };

    // Функции для работы с выбранными строками
    const handleRowSelection = (rowId, checked) => {
        setSelectedRows(prev => {
            const newSet = new Set(prev);
            if (checked) {
                newSet.add(rowId);
            } else {
                newSet.delete(rowId);
            }
            return newSet;
        });
    };

    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedRows(new Set(filteredData.map(row => row.id)));
        } else {
            setSelectedRows(new Set());
        }
    };

    // Обработчик сортировки
    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            // Переключаем направление сортировки
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Устанавливаем новый столбец для сортировки
            setSortBy(columnId);
            setSortDirection('asc');
        }
    };

    // Обработчики для управления видимостью столбцов
    const handleColumnMenuOpen = (event) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnVisibilityChange = (columnId, visible) => {
        setVisibleColumns(prev => ({
            ...prev,
            [columnId]: visible
        }));
    };

    const handleShowAllColumns = () => {
        setVisibleColumns(columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}));
    };

    const handleHideAllColumns = () => {
        // Оставляем хотя бы первый столбец видимым
        const newVisibility = columns.reduce((acc, col, index) => ({
            ...acc,
            [col.id]: index === 0
        }), {});
        setVisibleColumns(newVisibility);
    };

    // Получаем только видимые столбцы
    const getVisibleColumns = useCallback(() => {
        return columns.filter(col => visibleColumns[col.id]);
    }, [visibleColumns]);

    // Функция для вычисления динамической ширины столбцов
    const getColumnWidths = useCallback(() => {
        const visibleCols = columns.filter(col => visibleColumns[col.id]);
        if (visibleCols.length === 0) return {};

        // Получаем ширину экрана
        const screenWidth = window.innerWidth;
        const containerPadding = 32; // Отступы контейнера
        const availableWidth = screenWidth - containerPadding;

        // Вычисляем сумму минимальных ширин
        const totalMinWidth = visibleCols.reduce((sum, col) => sum + col.minWidth, 0);

        // Если доступная ширина больше суммы минимальных ширин, распределяем оставшееся место
        if (availableWidth > totalMinWidth) {
            const extraWidth = availableWidth - totalMinWidth;
            const flexibleCols = visibleCols.filter(col => !['id', 'cost'].includes(col.id)); // Не растягиваем узкие столбцы
            const extraPerCol = flexibleCols.length > 0 ? extraWidth / flexibleCols.length : 0;

            return visibleCols.reduce((acc, col) => {
                const isFlexible = flexibleCols.includes(col);
                acc[col.id] = isFlexible ? col.minWidth + extraPerCol : col.minWidth;
                return acc;
            }, {});
        }

        // Если места мало, используем минимальные ширины
        return visibleCols.reduce((acc, col) => {
            acc[col.id] = col.minWidth;
            return acc;
        }, {});
    }, [visibleColumns]);

    // Пересчитываем ширины столбцов при изменении видимости
    useEffect(() => {
        setColumnWidths(getColumnWidths());
    }, [getColumnWidths]);

    // Пересчитываем ширины при изменении размера окна
    useEffect(() => {
        const handleResize = () => {
            setColumnWidths(getColumnWidths());
        };

        window.addEventListener('resize', handleResize);
        // Начальный расчет
        setColumnWidths(getColumnWidths());

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [getColumnWidths]);

    // const handleBulkDelete = async () => {
    //     if (selectedRows.size === 0) return;

    //     const selectedObjects = filteredData.filter(row => selectedRows.has(row.id));
    //     const objectNames = selectedObjects.map(obj => obj.id).join(', ');

    //     if (window.confirm(`Вы уверены, что хотите удалить ${selectedRows.size} объектов (${objectNames})?`)) {
    //         try {
    //             setLoading(true);

    //             // Удаляем объекты по одному
    //             const deletePromises = selectedObjects.map(async (object) => {
    //                 const response = await $authHost.delete(`/apig/reestr/${object.id}`, {
    //                     data: {
    //                         modifier: user.id
    //                     }
    //                 });
    //                 return { id: object.id, success: response.data.success };
    //             });

    //             const results = await Promise.all(deletePromises);
    //             const failedDeletes = results.filter(result => !result.success);

    //             if (failedDeletes.length === 0) {
    //                 // Обновляем данные - убираем удаленные объекты
    //                 // setData(prevData => prevData.filter(item => !selectedRows.has(item.id)));
    //                 setSelectedRows(new Set());
    //                 alert(`Успешно удалено ${selectedRows.size} объектов`);
    //             } else {
    //                 alert(`Ошибка при удалении ${failedDeletes.length} объектов`);
    //             }
    //         } catch (error) {
    //             console.error('Error bulk deleting objects:', error);
    //             alert(`Ошибка при массовом удалении: ${error.message}`);
    //         } finally {
    //             setLoading(false);
    //         }
    //     }
    // };

    const handleCancelChanges = () => {
        // Восстанавливаем оригинальные данные
        // setData(prev => {
        //     let newData = [...prev];
        //     editedRows.forEach(editedRow => {
        //         const index = newData.findIndex(item => item.id === editedRow.id);
        //         if (index !== -1) {
        //             newData[index] = { ...newData[index], ...editedRow.originalData };
        //         }
        //     });
        //     return newData;
        // });

        // Очищаем editedRows
        setEditedRows([]);
    };

    const handleSaveChanges = async () => {
        try {
            const changes = []
            editedRows.forEach(row => {
                const id = row.id
                if (row.changes.part_elimin_categ) changes.push({ id: id, field: 'part_elimin_categ', value: row.changes.part_elimin_categ })
                if (row.changes.status) changes.push({ id: id, field: 'prescription_status', value: row.changes.part_elimin_categ })
                if (row.changes.status_log) {
                    if (!hasRejectedRows) {
                        changes.push({ id: id, field: 'status_log', value: row.changes.status_log })
                    } else {

                        const rejectedCodes = hasRejectedRows.filter(r => r.startsWith(String(id))).map(r => r.split('_')[1])
                        if (!rejectedCodes || rejectedCodes.length === 0) {
                            changes.push({ id: id, field: 'status_log', value: row.changes.status_log })

                        } else {
                            // Create a deep copy of status_log to avoid mutating read-only objects
                            const status_log_with_reason = JSON.parse(JSON.stringify(row.changes.status_log || {}))

                            rejectedCodes.map(rc => {
                                if (status_log_with_reason[rc] && !status_log_with_reason[rc].reason) {
                                    status_log_with_reason[rc] = {
                                        ...status_log_with_reason[rc],
                                        reason: rejectionReason
                                    }
                                }
                            })
                            changes.push({ id: id, field: 'status_log', value: status_log_with_reason })

                        }


                    }
                }
            })

            // Отправляем изменения на сервер
            // const response = await $authHost.put('/apig/reestr/batch', {
            //     updates: editedRows.map(row => ({
            //         id: row.id,
            //         changes: row.changes,
            //         modifier: user.id
            //     }))
            // });

            const response = await $authHost.post('/apig/state/accounting/part-elimin-categ', {
                user_id: user.vio_id,
                changes: changes
            }, {
                timeout: 30000
            });

            if (response.status >= 200 && response.status < 300) {
                // Очищаем editedRows после успешного сохранения
                setEditedRows([]);
                setHasRejectedRows([])
                setRejectionReason('')
                dispatch(fetchAccounting())
                // Обновляем данные с сервера
                // fetchData();
            } else {
                throw new Error(response.data.error || 'Ошибка при сохранении');
            }
        } catch (error) {
            console.error('Error saving changes:', error);
            alert(`Ошибка при сохранении: ${error.message}`);
        }
    };

    const handleToggleDarkMode = () => {
        dispatch(toggleDarkMode());
    };

    // Функции для управления продвинутыми фильтрами
    const handleApplyAdvancedFilters = () => {
        // Фильтры применяются автоматически через useEffect
        setShowAdvancedFilters(false);
    };

    // Функция-обертка для setFilterMode с сохранением в localStorage
    const handleFilterModeChange = (newMode) => {
        setFilterMode(newMode);
        storeSettings({ advancedFilters, filterMode: newMode });
    };

    const handleClearAdvancedFilters = () => {
        setAdvancedFilters([]);
        storeSettings({ advancedFilters: [], filterMode });
    };

    const getActiveAdvancedFiltersCount = () => {
        return advancedFilters.length;
    };

    const addFilter = (filterId) => {
        const filter = availableFilters.find(f => f.id === filterId);
        if (filter) {
            const newFilter = {
                id: filterId,
                label: filter.label,
                type: filter.type,
                options: filter.options,
                operator: filter.type === 'date' || filter.type === 'number' ? 'equals' :
                    filter.type === 'text' ? 'contains' : null,
                value: filter.type === 'date' || filter.type === 'number' ? null :
                    filter.type === 'boolean' ? '' :
                        filter.type === 'text' ? '' :
                            filter.type === 'multi-select' ? [] : [],
                value2: filter.type === 'date' ? null : null, // Для фильтра "между"
                logic: filter.type === 'multi-select' ? 'or' : null,
                enabled: true
            };
            const newFilters = [...advancedFilters, newFilter];
            setAdvancedFilters(newFilters);
            storeSettings({ advancedFilters: newFilters, filterMode });
        }
    };

    const removeFilter = (filterId) => {
        const newFilters = advancedFilters.filter(f => f.id !== filterId);
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters, filterMode });
    };

    const updateFilter = (filterId, updates) => {
        const newFilters = advancedFilters.map(f =>
            f.id === filterId ? { ...f, ...updates } : f
        );
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters, filterMode });
    };

    const getAvailableFiltersForSelect = () => {
        const usedFilterIds = advancedFilters.map(f => f.id);
        return availableFilters.filter(f => !usedFilterIds.includes(f.id));
    };

    // Функция для определения текущего статуса по фильтру
    const getCurrentStatusFromFilter = () => {
        const statusFilter = advancedFilters.find(filter => filter.id === 'status');

        if (!statusFilter || !statusFilter.value || statusFilter.value.length === 0) {
            return 'Отобразить все';
        }

        // Получаем массив значений из фильтра
        const filterValues = statusFilter.value.map(v => v.name || v.id || v).sort();

        // Ищем статус с полным соответствием фильтра
        for (const [statusKey, statusData] of Object.entries(statuses)) {
            const statusFilterValues = [...statusData.filter].sort();

            // Проверяем полное соответствие массивов
            if (statusFilterValues.length === filterValues.length &&
                statusFilterValues.every((val, index) => val === filterValues[index])) {
                return statusKey;
            }
        }

        return 'Другие';
    };

    // Функция для обработки клика по кнопке быстрой фильтрации
    const handleQuickFilterClick = () => {
        const currentStatusKey = getCurrentStatusFromFilter();
        const currentStatus = statuses[currentStatusKey];

        // Если текущий статус "Другие", переходим к "Актуальные"
        if (currentStatusKey === 'Другие') {
            const nextStatus = 'Актуальные';
            const newFilters = advancedFilters.filter(f => f.id !== 'status');

            if (statuses[nextStatus].filter.length > 0) {
                const statusFilter = {
                    id: 'status',
                    label: 'Отметка о выполнении',
                    type: 'multi-select',
                    operator: null,
                    value: statuses[nextStatus].filter.map(status => ({ id: status, name: status, label: status })),
                    value2: null,
                    logic: 'equals',
                    enabled: true
                };
                newFilters.push(statusFilter);
            }

            setAdvancedFilters(newFilters);
            return;
        }

        const nextStatusKey = currentStatus.next;

        // Находим следующий статус
        let nextStatus = Object.keys(statuses).find(key => {
            // Для значений next, которые могут быть сокращенными
            const statusKeys = {
                'not_completed': 'Не устранено',
                'half_completed': 'Частично устранено',
                'rejected': 'Отклонено',
                'ready': 'Готово к проверке',
                'completed': 'Устранено',
                'all': 'Отобразить все',
                'actual': 'Актуальные'
            };
            return key === statusKeys[nextStatusKey] || key === nextStatusKey;
        });

        if (!nextStatus) {
            nextStatus = 'Актуальные'; // fallback
        }

        // Удаляем текущий фильтр по статусу, если есть
        const newFilters = advancedFilters.filter(f => f.id !== 'status');

        // Добавляем новый фильтр, если не "Отобразить все"
        if (statuses[nextStatus].filter.length > 0) {
            const statusFilter = {
                id: 'status',
                label: 'Отметка о выполнении',
                type: 'multi-select',
                operator: null,
                value: statuses[nextStatus].filter.map(status => ({ id: status, name: status, label: status })),
                value2: null,
                logic: 'equals',
                enabled: true
            };
            newFilters.push(statusFilter);
        }

        setAdvancedFilters(newFilters);
    };

    // Функция для подсчета количества групп и строк для кнопки быстрой фильтрации
    const getQuickFilterCounts = () => {
        const groupCount = groupedData.groupCounts.length;
        const totalRows = groupedData.groupedData.length;
        return { groupCount, totalRows };
    };

    // Функции для управления меню экспорта
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    const handleExportFormatSelect = (format) => {
        handleExportMenuClose();
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };
    // Функция экспорта в Excel для AccountingSheetView
    const exportToExcel = async () => {
        try {
            // Создаем новую рабочую книгу ExcelJS
            const workbook = new ExcelJS.Workbook();
            workbook.created = new Date();
            workbook.modified = new Date();
            workbook.title = 'Предписания авторского надзора ООО "КОМОКС"';

            // Создаем лист
            const worksheet = workbook.addWorksheet('Предписания авторского надзора');

            // Закрепляем первую строку (общие заголовки)
            worksheet.views = [
                { state: 'frozen', ySplit: 1 }
            ];


            // Закрепление заголовков будет обновлено после добавления информационных строк

            // Определяем заголовки колонок
            const headers = [
                '№',
                'Номер опоры',
                'Готово/Отклонено',
                'Дата готово/отклонено',
                'Пользователь готово/отклонено',
                'Причина отклонения',
                'Устранено',
                'Дата устранено',
                'Пользователь устранено',
                'Коды чек-листа',
                'Описание кодов чек-листа',
                'Указания и сроки их выполнения',
                'Стоимость',
                'Отметка о выполнении',
                'Дата отметки',
                'Авторский надзор',
                'Дата создания',
            ];

            // Добавляем заголовки
            // const headerRow = worksheet.addRow(headers);

            // // Стилизуем заголовки
            // headerRow.eachCell((cell, colNumber) => {
            //     cell.fill = {
            //         type: 'pattern',
            //         pattern: 'solid',
            //         fgColor: { argb: 'FFCD5C5C' }
            //     };
            //     cell.font = {
            //         name: 'Arial',
            //         size: 10,
            //         bold: true,
            //         color: { argb: 'FFFFFFFF' }
            //     };
            //     cell.alignment = {
            //         vertical: 'middle',
            //         horizontal: 'center',
            //         wrapText: true
            //     };
            //     cell.border = {
            //         top: { style: 'thin' },
            //         left: { style: 'thin' },
            //         bottom: { style: 'thin' },
            //         right: { style: 'thin' }
            //     };

            // });


            // Группируем данные по объектам для экспорта
            // const exportData = [];

            // Группируем данные по объектам как в react-virtuoso
            const groupedByObjects = {};
            filteredData.forEach(item => {
                // console.log(activeAccoutingsProjects, item)
                // const objectName = activeAccoutingsProjects[item.tp.fid]?.label || 'Неизвестный объект';
                const objectName = `${item.ps.name} ${item.ps.operating_voltage} ${item.tp.name} ${item.tp.town}`

                if (!groupedByObjects[objectName]) {
                    groupedByObjects[objectName] = [];
                }

                item.pillars.forEach(pillar => {
                    groupedByObjects[objectName].push(pillar);
                });
            });
            const sortedGroups = Object.keys(groupedByObjects).sort();

            // Собираем все данные в один массив
            let allRows = [];
            let objectRows = []

            sortedGroups.forEach((objectName, objectIndex) => {
                const objectData = groupedByObjects[objectName];

                // Добавляем строку проекта
                const objectRow = [objectName];
                allRows.push(objectRow);
                objectRows.push(allRows.length + 1)

                const tableRows = []

                // Добавляем данные объектов проекта
                objectData.forEach(item => {
                    const accounting = item.accounting;
                    if (!accounting) return;

                    // Обработка множественных checklist элементов
                    if (accounting.checklist && accounting.checklist.length > 0) {
                        // Создаем строку для каждого элемента checklist
                        accounting.checklist.forEach((checklistCode, index) => {
                            const rule = rulesMap[checklistCode];
                            if (!rule) return;

                            // Получаем статус из status_log для конкретного checklist item
                            const statusLog = accounting.status_log?.[checklistCode];
                            const _statusLog = accounting._status_log?.[checklistCode];
                            const part_elimin_categ = accounting.part_elimin_categ || { ready: [], success: [], rejected: [] };

                            // Определяем статусы ready/rejected/success
                            const isReady = statusLog?.ready || part_elimin_categ.ready?.includes(checklistCode) || false;
                            const isRejected = statusLog?.rejected || part_elimin_categ.rejected?.includes(checklistCode) || false;
                            const isSuccess = statusLog?.success || part_elimin_categ.success?.includes(checklistCode) || false;

                            // Формируем данные для готово/отклонено
                            let readyRejectedStatus = '';
                            let readyRejectedDate = '';
                            let rejectionReason = '';
                            let readyRejectedUser = '';

                            if (isRejected) {
                                readyRejectedStatus = 'Отклонено';
                                rejectionReason = statusLog.reason;
                                if (_statusLog && (statusLog?.ready || statusLog?.rejected)) {
                                    readyRejectedDate = _statusLog.date || '';
                                    readyRejectedUser = _statusLog.user ? `${_statusLog.user.short_fio} (${_statusLog.user.post_for_select})` : '';
                                }
                            } else if (isReady) {
                                readyRejectedStatus = 'Готово к проверке';
                                if (_statusLog && (statusLog?.ready || statusLog?.rejected)) {
                                    readyRejectedDate = _statusLog.date || '';
                                    readyRejectedUser = _statusLog.user ? `${_statusLog.user.short_fio} (${_statusLog.user.post_for_select})` : '';
                                }
                            }

                            // Формируем данные для устранено
                            let successStatus = '';
                            let successDate = '';
                            let successUser = '';

                            if (isSuccess) {
                                successStatus = 'Устранено';
                                if (_statusLog && statusLog?.success) {
                                    successDate = _statusLog.date || '';
                                    successUser = _statusLog.user ? `${_statusLog.user.short_fio} (${_statusLog.user.post_for_select})` : '';
                                }
                            }

                            tableRows.push([
                                accounting.id,
                                item.name,
                                readyRejectedStatus,
                                readyRejectedDate,
                                readyRejectedUser,
                                rejectionReason,
                                successStatus,
                                successDate,
                                successUser,
                                checklistCode,
                                rule.name || '',
                                rule.description || '',
                                parseFloat(rule.cost) || 0.00,
                                accounting.status || '',
                                accounting.status_date || '',
                                `${accounting.creator.short_fio} (${accounting.creator.post_for_select})` || '',
                                accounting.created || '',
                            ]);
                        });
                    }

                });

                allRows.push(...tableRows);
            });

            // Добавляем одну большую таблицу со всеми данными
            const endRow = allRows.length + 1; // +1 для заголовков
            worksheet.addTable({
                name: 'ObjectsReestrTable',
                ref: `A1:O${endRow}`,
                headerRow: true,
                totalsRow: false,
                style: { theme: 'TableStyleLight3', showRowStripes: true },
                columns: headers.map(h => ({ name: h, filterButton: true })),
                rows: allRows
            });
            const colWidths = [8, 30, 15, 18, 20, 20, 15, 18, 20, 10, 30, 35, 15, 15, 18, 25, 18]
            const centerCols = [1, 2, 3, 4, 5, 7, 8, 9, 10, 13, 14, 15, 16, 17]


            // Устанавливаем ширину столбцов
            colWidths.forEach((width, index) => {
                const column = worksheet.getColumn(index + 1);
                column.width = width;
            });

            const dateCols = [4, 8, 15, 17]
            dateCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = 'dd/mm/yyyy'
            })

            const currencyCols = [13]
            currencyCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = '# ##0.00'
            })

            const numberCols = [1]
            numberCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = '0'
            })

            const textCols = [2, 3, 5, 6, 7, 9, 10, 11, 12, 14, 16]
            textCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = ''
            })

            const statusCols = [3, 7, 14]
            const codeCol = 10
            const codeColors = {
                'П': 'FF9333EA',
                'Г': 'FF10B981',
                'С': 'FF3B82F6',
                'Р': 'FFF6223A'
            };



            // Устанавливаем черный цвет текста для всех ячеек таблицы
            for (let rowNumber = 1; rowNumber <= endRow; rowNumber++) {
                const row = worksheet.getRow(rowNumber);
                // row.height = undefined

                row.eachCell((cell, colNumber) => {
                    let color = 'FF000000'
                    if (statusCols.includes(colNumber) && rowNumber !== 1) color = statuses[cell.text]?.argb || 'FF000000'
                    if (colNumber === codeCol) color = codeColors[cell.text[0]] || 'FF000000'
                    cell.font = {
                        ...cell.font,
                        name: 'Arial',
                        size: objectRows.includes(rowNumber) || rowNumber === 1 ? 11 : 10,
                        bold: objectRows.includes(rowNumber) || rowNumber === 1 || colNumber === codeCol || statusCols.includes(colNumber),
                        color: { argb: color } // Черный цвет

                    };
                    cell.alignment = {
                        horizontal: !objectRows.includes(rowNumber) && (rowNumber === 1 || centerCols.includes(colNumber)) ? 'center' : 'left',
                        vertical: 'middle',
                        wrapText: !objectRows.includes(rowNumber) || rowNumber == 1 ? true : false
                    }


                });
                row.height = undefined; // Это включает автоподбор

                if (typeof row.commit === 'function') row.commit();
            }


            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Предписания авторского надзора ООО "КОМОКС" от ${dateStr}.xlsx`;

            // Экспортируем файл
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);


            console.log('Excel файл успешно создан:', fileName);
        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };


    // Функция экспорта в PDF
    const exportToPDF = () => {
        try {
            // Группируем данные по проектам
            const groupedByObjects = {};
            filteredData.forEach(item => {
                // const objectName = activeAccoutingsProjects[item.tp.fid]?.label || 'Неизвестный объект';
                const objectName = `${item.ps.name} ${item.ps.operating_voltage} ${item.tp.name} ${item.tp.town}`

                if (!groupedByObjects[objectName]) {
                    groupedByObjects[objectName] = [];
                }

                item.pillars.forEach(pillar => {
                    groupedByObjects[objectName].push(pillar);
                });
            });

            // Сортируем группы по названию проекта
            const sortedGroups = Object.keys(groupedByObjects).sort();

            // Создаем PDF документ
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            pdf.setFont("arial", "normal");
            pdf.setLanguage('ru')

            // Настройки страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 5;
            const contentWidth = pageWidth - 2 * margin;
            const contentHeight = pageHeight - 2 * margin;

            // Заголовок документа
            pdf.setFontSize(14);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(150, 54, 52);
            pdf.text('Предписания авторского надзора ООО "КОМОКС"', pageWidth / 2, 10, { align: 'center' });

            // Информация о документе
            pdf.setFontSize(10);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(100, 100, 100);
            const currentDate = new Date().toLocaleDateString('ru-RU');
            pdf.text(`Дата экспорта: ${currentDate}`, pageWidth - margin, 15, { align: 'right' });
            pdf.text(`Всего объектов: ${groupedData.groupCounts.length}`, margin, 15);

            let currentY = 15;
            let pageNumber = 1;
            let totalPages = 1; // Будет обновлено после создания всех страниц

            // Определяем заголовки таблицы (как в Excel)
            const headers = [
                { content: '№', colSpan: 1 },
                { content: 'Номер опоры', colSpan: 1 },
                { content: 'Готово/Отклонено', colSpan: 3 },
                { content: 'Устранено', colSpan: 2 },
                { content: 'Коды чек-листа', colSpan: 1 },
                { content: 'Описание кодов чек-листа', colSpan: 1 },
                { content: 'Указания и сроки их выполнения', colSpan: 1 },
                { content: 'Стоимость', colSpan: 1 },
                { content: 'Отметка о выполнении', colSpan: 1 },
                { content: 'Авторский надзор', colSpan: 1 },
            ];

            // Добавляем данные по группам
            sortedGroups.forEach((objectName, objectIndex) => {
                const objectData = groupedByObjects[objectName];

                // Проверяем, нужно ли добавить новую страницу
                // if (currentY > pageHeight - 80) {
                //     pdf.addPage();
                //     pageNumber++;
                //     currentY = 10;

                //     // Начинаем с верхней части новой страницы
                //     currentY = 10;
                // }

                // // Заголовок проекта (как в Excel - с заливкой)
                // pdf.setFontSize(11);
                // pdf.setFont("arial", "bold");
                // pdf.setTextColor(0, 0, 0);

                // // Рисуем фон для заголовка проекта (светло-голубой как в Excel)
                // pdf.setFillColor(242, 220, 219); // E6F3FF
                // pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 8, 'F');

                // // Рисуем границы
                // pdf.setDrawColor(208, 215, 224); // D0D7E0
                // pdf.setLineWidth(0.1);
                // pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 8);

                // pdf.text(objectName, pageWidth / 2, currentY + 2, { align: 'center' });
                // currentY += 12;

                const tableData = [
                    [{ content: objectName, colSpan: 13, styles: { halign: 'center', fontStyle: 'bold', fontSize: 9 } }]
                ]

                objectData.forEach(item => {
                    const accounting = item.accounting;
                    if (!accounting) return;

                    // Обработка множественных checklist элементов
                    if (accounting.checklist && accounting.checklist.length > 0) {
                        // Создаем строку для каждого элемента checklist
                        accounting.checklist.forEach((checklistCode, index) => {
                            const rule = rulesMap[checklistCode];
                            if (!rule) return;

                            // Получаем статус из status_log для конкретного checklist item
                            const statusLog = accounting.status_log?.[checklistCode];
                            const _statusLog = accounting._status_log?.[checklistCode];
                            const part_elimin_categ = accounting.part_elimin_categ || { ready: [], success: [], rejected: [] };

                            // Определяем статусы ready/rejected/success
                            const isReady = statusLog?.ready || part_elimin_categ.ready?.includes(checklistCode) || false;
                            const isRejected = statusLog?.rejected || part_elimin_categ.rejected?.includes(checklistCode) || false;
                            const isSuccess = statusLog?.success || part_elimin_categ.success?.includes(checklistCode) || false;

                            // Формируем данные для готово/отклонено
                            let readyRejectedStatus = '';
                            let readyRejectedDate = '';
                            let rejectionReason = '';
                            let readyRejectedUser = '';

                            if (isRejected) {
                                readyRejectedStatus = 'Отклонено';
                                rejectionReason = statusLog.reason;
                                if (_statusLog && (statusLog?.ready || statusLog?.rejected)) {
                                    readyRejectedDate = _statusLog.date || '';
                                    readyRejectedUser = _statusLog.user ? `${_statusLog.user.short_fio} (${_statusLog.user.post_for_select})` : '';
                                }
                            } else if (isReady) {
                                readyRejectedStatus = 'Готово к проверке';
                                if (_statusLog && (statusLog?.ready || statusLog?.rejected)) {
                                    readyRejectedDate = _statusLog.date || '';
                                    readyRejectedUser = _statusLog.user ? `${_statusLog.user.short_fio} (${_statusLog.user.post_for_select})` : '';
                                }
                            }

                            // Формируем данные для устранено
                            let successStatus = '';
                            let successDate = '';
                            let successUser = '';

                            if (isSuccess) {
                                successStatus = 'Устранено';
                                if (_statusLog && statusLog?.success) {
                                    successDate = _statusLog.date || '';
                                    successUser = _statusLog.user ? `${_statusLog.user.short_fio} (${_statusLog.user.post_for_select})` : '';
                                }
                            }

                            tableData.push([
                                accounting.id,
                                item.name,
                                readyRejectedStatus,
                                [readyRejectedDate, readyRejectedUser].join('\n'),
                                rejectionReason,
                                successStatus,
                                [successDate, successUser].join('\n'),
                                checklistCode,
                                rule.name || '',
                                rule.description || '',
                                new Intl.NumberFormat('ru-RU', {
                                    style: 'currency',
                                    currency: 'RUB',
                                }).format(rule.cost || 0),
                                [accounting.status, accounting.status_date].join('\n'),
                                [`${accounting.creator.short_fio} (${accounting.creator.post_for_select})`, accounting.created].join('\n') || ''
                            ]);
                        });
                    }

                });



                // Добавляем таблицу с улучшенным форматированием
                autoTable(pdf, {
                    head: [headers],
                    body: tableData,
                    startY: currentY,
                    margin: { left: margin, right: margin },
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    styles: {
                        fontSize: 8,
                        cellPadding: 1,
                        lineColor: [208, 215, 224], // D0D7E0 - как в Excel
                        lineWidth: 0.1,
                        textColor: [0, 0, 0],
                        font: 'arial',
                        fontStyle: 'normal'
                    },
                    headStyles: {
                        fillColor: [150, 54, 52], // Синий заголовок как в Excel
                        textColor: [255, 255, 255],
                        font: 'arial',
                        fontStyle: 'bold',
                        fontSize: 9,
                        cellPadding: 1,
                        halign: 'center',
                        valign: 'middle'
                    },
                    alternateRowStyles: {
                        fillColor: [242, 220, 219] // E6F3FF - светло-голубой для четных строк
                    },
                    columnStyles: {
                        0: { cellWidth: 10, valign: 'middle', halign: 'center' },   // №
                        1: { cellWidth: 28, valign: 'middle', halign: 'center' },  // Номер опоры
                        2: { cellWidth: 20, valign: 'middle', halign: 'center' },  // Готово/Отклонено
                        3: { cellWidth: 24, valign: 'middle', halign: 'center' },  // Готово/Отклонено
                        4: { cellWidth: 20, valign: 'middle', halign: 'center' },  // Готово/Отклонено
                        5: { cellWidth: 20, valign: 'middle', halign: 'center' },  // Устранено
                        6: { cellWidth: 24, valign: 'middle', halign: 'center' },  // Устранено
                        7: { cellWidth: 16, valign: 'middle', halign: 'center' },  // Коды чек-листа
                        8: { cellWidth: 30, valign: 'middle' },  // Описание кодов чек-листа
                        9: { cellWidth: 38, valign: 'middle' },  // Указания и сроки их выполнения
                        10: { cellWidth: 12, valign: 'middle', halign: 'center' },  // Стоимость
                        11: { cellWidth: 22, valign: 'middle', halign: 'center' },  // Отметка о выполнении
                        12: { cellWidth: 24, valign: 'middle', halign: 'center' },  // Авторский надзор

                    },
                    didDrawPage: function (data) {
                        // Функция будет вызвана для каждой страницы, но номера добавим позже
                    }
                });

                // Получаем позицию после таблицы
                const tableEndY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY : currentY + 50;
                currentY = tableEndY + 5;

                // Не добавляем разделитель между проектами (как в Excel)
            });

            // Получаем общее количество страниц
            totalPages = pdf.getNumberOfPages();

            // Добавляем номера страниц на все страницы
            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                pdf.setPage(pageNum);

                // Добавляем номер страницы внизу
                pdf.setFontSize(8);
                pdf.setFont("arial", "normal");
                pdf.setTextColor(100, 100, 100);
                pdf.text(`Стр. ${pageNum} из ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
            }

            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Предписания авторского надзора ООО "КОМОКС" от ${dateStr}.pdf`;

            // Сохраняем PDF
            pdf.save(fileName);
            console.log('PDF файл успешно создан с форматированием как в Excel');
        } catch (error) {
            console.error('Ошибка при экспорте в PDF:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };
    return (
        <Box sx={{
            bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',

            p: 0,
            m: 0,
            display: 'flex',
            flex: 1,
            height: '100%',
            flexDirection: 'column',
            // overflow: 'hidden',
            width: '100%'

        }}>
            <Paper sx={{
                display: 'flex',
                flexDirection: 'column',
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                borderRadius: 0,
                boxShadow: 'none',
                overflow: 'hidden',
                border: 'none',
                width: '100%',
                minHeight: '100%',
            }}>
                {/* Первая строка - заголовок, поиск, темная тема */}
                <Box sx={{
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    p: { xs: 0.5, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    // gap: { xs: 1, md: 1 },
                }}>
                    {!fromStartPage && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'row', alignItems: 'center', gap: 1, width: '100%' }}>
                            <AssignmentIcon sx={{ fontSize: { xs: 24, md: 32 }, color: darkMode ? 'white' : 'black' }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.1, color: darkMode ? 'white' : 'black' }}>
                                Предписания авторского надзора ООО "КОМОКС"
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                        {/* <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            border: darkMode ? '2px solid #64b5f6' : '2px solid #1976d2'
                        }}>
                            <BusinessIcon sx={{
                                fontSize: '28px',
                                color: darkMode ? '#64b5f6' : '#1976d2'
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                color: darkMode ? '#f5f7fa' : '#333',
                                mb: 0.5,
                            }}>
                                Реестр объектов
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.875rem'
                            }}>
                                Управление объектами строительства
                            </Typography>
                        </Box> */}

                        {/* Кнопка для меню проектов */}
                        <TextField
                            size="small"
                            placeholder={selectedObjects.size > 0 ? `Выбрано объектов: ${[...selectedObjects].filter(f => f.split('-').length === 3).length}` : "Выбор объектов"}
                            value={treeSearchQuery}
                            onChange={(e) => setTreeSearchQuery(e.target.value)}
                            onClick={(e) => {
                                e.stopPropagation();
                                setProjectsFilterAnchor(e.currentTarget);
                            }}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {treeSearchQuery && (
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setTreeSearchQuery('');
                                                    }}
                                                    sx={{
                                                        color: darkMode ? '#bbb' : '#666',
                                                        '&:hover': {
                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                        }
                                                    }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </InputAdornment>
                                    ),
                                }
                            }}
                            sx={{
                                minWidth: { xs: 'auto', md: '300px' },
                                flex: { xs: 1, sm: 'none' },
                                '& .MuiInputBase-root': {
                                    bgcolor: darkMode ? '#1e1e1e' : '#f8fafc',
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1976d2'
                                    }
                                },
                                '& .MuiInputBase-input': {
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    cursor: 'pointer',
                                    '&::placeholder': {
                                        color: darkMode ? '#bbb' : '#666',
                                        opacity: 1
                                    }
                                }
                            }}
                        />
                    </Box>

                    {/* Меню с деревом проектов */}
                    <Popper
                        open={Boolean(projectsFilterAnchor)}
                        anchorEl={projectsFilterAnchor}
                        placement="bottom-start"
                        style={{
                            zIndex: 1300,
                            width: projectsFilterAnchor ? Math.max(projectsFilterAnchor.clientWidth, 350) : 350
                        }}
                    >
                        <Paper
                            ref={projectsFilterPopperRef}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                color: darkMode ? '#f5f7fa' : '#333',
                                maxHeight: '600px',
                                width: '400px',
                                overflow: 'auto',
                                border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                mt: 0.5
                            }}
                        >
                            {/* Заголовок с чипами */}
                            <Box sx={{ px: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                {/* <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    Фильтр по проектам
                                </Typography> */}
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Chip
                                        label="Отметить все"
                                        size="small"
                                        onClick={selectAllFilters}
                                        sx={{
                                            fontSize: '0.75rem',
                                            height: '24px',
                                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.2)'
                                            }
                                        }}
                                    />
                                    <Chip
                                        label="Снять все"
                                        size="small"
                                        onClick={clearAllFilters}
                                        sx={{
                                            fontSize: '0.75rem',
                                            height: '24px',
                                            bgcolor: darkMode ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)',
                                            color: darkMode ? '#f44336' : '#d32f2f',
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'
                                            }
                                        }}
                                    />

                                </Box>
                                <IconButton
                                    size="small"
                                    onClick={() => setProjectsFilterAnchor(null)}
                                    sx={{
                                        ml: 1,
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                        '&:hover': {
                                            backgroundColor: darkMode ? '#555' : '#e0e0e0'
                                        }
                                    }}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </Box>

                            {/* SimpleTreeView с чекбоксами */}
                            <Box sx={{ p: 1 }}>
                                {treeData.length > 0 ? (
                                    <SimpleTreeView
                                        sx={{
                                            '& .MuiTreeItem-root': {
                                                color: darkMode ? '#f5f7fa' : '#333',
                                            },
                                            '& .MuiTreeItem-content': {
                                                py: '0',
                                                gap: 0,
                                                borderRadius: '4px',
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                },
                                                '&.Mui-selected': {
                                                    bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
                                                }
                                            },
                                            '& .MuiTreeItem-group': {
                                                // marginLeft: '16px',
                                                borderLeft: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                                // paddingLeft: '8px'
                                            },
                                            '& .MuiTreeItem-iconContainer': {
                                                color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                                // marginRight: '4px'
                                            }
                                        }}
                                    >
                                        {treeData.map(item => renderTreeItem(item))}
                                    </SimpleTreeView>
                                ) : (
                                    <Box sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{
                                            fontSize: '0.7rem',
                                            lineHeight: 1.2
                                        }}>
                                            Нет данных о проектах
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Popper>

                    {/* Центральная часть - поиск и KDP селект */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2,
                        maxWidth: { xs: '100%', md: '600px' },
                        minWidth: { xs: '100%', sm: '400px', md: '500px' },
                        order: { xs: 3, md: 0 }
                    }}>


                        {/* Поиск */}
                        <Box sx={{
                            flex: 1,
                            position: 'relative'
                        }}>
                            <TextField
                                size="small"
                                placeholder="Поиск..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: <SearchIcon sx={{
                                            mr: 1,
                                            color: darkMode ? '#bbb' : 'text.secondary',
                                            fontSize: { xs: '16px', md: '20px' }
                                        }} />,
                                        endAdornment: searchQuery && (
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchQuery('')}
                                                sx={{
                                                    color: darkMode ? '#bbb' : '#666',
                                                    '&:hover': {
                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                    }
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: darkMode ? '#1e1e1e' : '#f8fafc',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&::placeholder': {
                                            color: darkMode ? '#bbb' : '#666',
                                            opacity: 1
                                        }
                                    }
                                }}
                            />
                            {/* Индикатор результатов поиска */}
                            {/* {searchQuery && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        mt: 0.5,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    Найдено: {filteredData.length} из {data.length}
                                </Typography>
                            )} */}
                        </Box>
                        {/* Правая часть - кнопки управления */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* Кнопка настроек столбцов */}
                            <Tooltip title="Настроить видимость столбцов">
                                <IconButton
                                    onClick={handleColumnMenuOpen}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#666',
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <ViewColumnIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
                                </IconButton>
                            </Tooltip>

                            {/* Кнопка темной темы */}
                            {/* <IconButton
                                onClick={handleToggleDarkMode}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#666',
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {darkMode ? <LightModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} /> : <DarkModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />}
                            </IconButton> */}
                            {!fromStartPage && (
                                <IconButton
                                    onClick={onClose}
                                    size="small"
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        p: { xs: 0.5, md: 1 },
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                                        }
                                    }}
                                >
                                    <CloseIcon sx={{ fontSize: { xs: '18px', md: '20px' } }} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>


                </Box>

                {/* Вторая строка - панель инструментов */}
                <Box sx={{
                    p: { xs: 1, md: 1 },
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    bgcolor: darkMode ? '#252525' : '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1, md: 2 },
                }}>
                    {/* Индикатор активных фильтров */}
                    {/* {(searchQuery || filterMode !== 'all' || sortMode !== 'created_desc' || selectedKdp.length > 0) && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            p: 1,
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                            borderRadius: 1,
                            border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                        }}>
                            <Typography variant="caption" sx={{ color: darkMode ? '#64b5f6' : '#1976d2', fontWeight: 600 }}>
                                Активные фильтры:
                            </Typography>
                            {searchQuery && (
                                <Chip
                                    label={`Поиск: "${searchQuery}"`}
                                    size="small"
                                    onDelete={() => setSearchQuery('')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {selectedKdp.length > 0 && (
                                <Chip
                                    label={`KDP: ${selectedKdp.length} выбрано`}
                                    size="small"
                                    onDelete={() => setSelectedKdp([])}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {filterMode !== 'all' && (
                                <Chip
                                    label={`Фильтр: ${filterMode === 'my' ? 'Мои объекты' : 'Активные этапы'}`}
                                    size="small"
                                    onDelete={() => setFilterMode('all')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {sortMode !== 'created_desc' && (
                                <Chip
                                    label={`Сортировка: ${sortMode}`}
                                    size="small"
                                    onDelete={() => setSortMode('created_desc')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                        color: darkMode ? '#81c784' : '#388e3c',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                        </Box>
                    )} */}

                    {/* Основные элементы управления */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 1, md: 2 },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: 'space-between'
                    }}>
                        {/* Левая группа - кнопка создания и фильтры */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, md: 2 },
                            flex: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: 'wrap',
                            alignItems: { xs: 'stretch', sm: 'center' }
                        }}>
                            {/* Кнопка создания */}
                            {/* {user.roles?.includes('project_manager') && (
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenCreateDialog()}
                                    sx={{
                                        bgcolor: '#4caf50',
                                        color: 'white',
                                        px: 1.5,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: 1.5,
                                        boxShadow: '0 2px 4px rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#388e3c',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 8px rgba(76, 175, 80, 0.4)'
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    Создать объект
                                </Button>
                            )} */}
                            {/* Кнопка быстрой фильтрации */}
                            {(() => {
                                const currentStatusKey = getCurrentStatusFromFilter();
                                const currentStatus = currentStatusKey === 'Другие' ?
                                    { color: '#9E9E9E', icon: 'help_outline', text: 'Другие' } :
                                    statuses[currentStatusKey];

                                return (
                                    <Tooltip title="Видимые статусы | Количество объектов | Количество предписаний" >
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={getStatusIcon(currentStatus?.icon || 'list')}
                                            onClick={handleQuickFilterClick}
                                            sx={{
                                                bgcolor: currentStatus?.color || '#607D8B',
                                                color: '#fff',
                                                px: 1,
                                                py: 0.5,
                                                textTransform: 'none',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                borderRadius: 1,
                                                minWidth: 'auto',
                                                '&:hover': {
                                                    bgcolor: currentStatus?.color || '#607D8B',
                                                    filter: 'brightness(0.9)',
                                                    transform: 'translateY(-1px)',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            {currentStatus?.text || 'Отобразить все'}
                                            {(() => {
                                                const counts = getQuickFilterCounts();
                                                return counts.groupCount > 0 || counts.totalRows > 0 ? (
                                                    <Typography
                                                        component="span"
                                                        sx={{
                                                            ml: 0.5,
                                                            fontSize: '0.75rem',
                                                            opacity: 1
                                                        }}
                                                    >
                                                        | {counts.groupCount} | {counts.totalRows}
                                                    </Typography>
                                                ) : null;
                                            })()}
                                        </Button>
                                    </Tooltip>
                                );
                            })()}
                            {/* Кнопка продвинутых фильтров */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                sx={{
                                    borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    px: 1,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                    ...(getActiveAdvancedFiltersCount() > 0 && {
                                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                        borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                    })
                                }}
                            >
                                Фильтры
                                {getActiveAdvancedFiltersCount() > 0 && (
                                    <Chip
                                        label={getActiveAdvancedFiltersCount()}
                                        size="small"
                                        sx={{
                                            ml: 0.5,
                                            bgcolor: darkMode ? '#1976d2' : '#1976d2',
                                            color: '#fff',
                                            fontSize: '0.7rem',
                                            height: '16px',
                                            minWidth: '16px'
                                        }}
                                    />
                                )}
                            </Button>
                            {/* Объединенная кнопка экспорта с меню */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={handleExportMenuOpen}
                                disabled={filteredData.length === 0}
                                sx={{
                                    borderColor: darkMode ? '#1976d2' : '#1976d2',
                                    color: darkMode ? '#1976d2' : '#1976d2',
                                    px: 1,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        borderColor: darkMode ? '#1565c0' : '#1565c0',
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:disabled': {
                                        borderColor: darkMode ? '#666' : '#ccc',
                                        color: darkMode ? '#666' : '#ccc',
                                        transform: 'none',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Экспорт
                            </Button>
                            {/* Меню выбора формата экспорта */}
                            <Menu
                                anchorEl={exportMenuAnchor}
                                open={Boolean(exportMenuAnchor)}
                                onClose={handleExportMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                sx={{
                                    '& .MuiPaper-root': {
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                        boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    }
                                }}
                            >
                                <MenuItem
                                    onClick={() => handleExportFormatSelect('excel')}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                        }
                                    }}
                                >
                                    <FileDownloadIcon sx={{ mr: 1, color: '#4caf50' }} />
                                    Экспорт в Excel
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleExportFormatSelect('pdf')}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                        }
                                    }}
                                >
                                    <PictureAsPdfIcon sx={{ mr: 1, color: '#f44336' }} />
                                    Экспорт в PDF
                                </MenuItem>
                            </Menu>
                            {/* Фильтр */}
                            {/* <FormControl
                                size="small"
                                sx={{
                                    minWidth: { xs: 'auto', md: '150px' },
                                    flex: { xs: 1, sm: 'none' },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        '&.Mui-focused': {
                                            color: '#1976d2'
                                        }
                                    }
                                }}
                            >
                                <InputLabel>Фильтр</InputLabel>
                                <Select
                                    value={filterMode}
                                    size='small'
                                    onChange={(e) => setFilterMode(e.target.value)}
                                    label="Фильтр"
                                    startAdornment={<FilterIcon sx={{
                                        mr: 1,
                                        color: darkMode ? '#bbb' : 'text.secondary',
                                        fontSize: '18px'
                                    }} />}
                                    sx={{
                                        fontSize: '0.75rem',
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.7rem'
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                                border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.75rem',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Все объекты ({data.length})</MenuItem>
                                   
                                </Select>
                            </FormControl> */}

                            {/* Кнопка сброса фильтров */}
                            {/* {(searchQuery || filterMode !== 'all' || selectedKdp.length > 0) && (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setFilterMode('all');
                                        setSelectedKdp([]);
                                    }}
                                    sx={{
                                        mb: 1,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        '&:hover': {
                                            color: darkMode ? '#f5f7fa' : '#333',
                                        }
                                    }}
                                >
                                    Сбросить фильтры
                                </Button>
                            )} */}
                        </Box>

                        {/* Правая группа - кнопка редактирования */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, md: 2 },
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            justifyContent: 'space-between'
                        }}>
                            {/* Индикатор режима редактирования */}
                            {/* {editMode && (
                                <Chip
                                    label="Режим редактирования"
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                    }}
                                />
                            )} */}

                            {/* Индикатор изменений */}
                            {/* {editedRows.length > 0 && (
                                <Chip
                                    label={`${editedRows.length} изменений`}
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                                    }}
                                />
                            )} */}

                            {/* Кнопки отмены и сохранения */}
                            {editedRows.length > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: { xs: 1, md: 2 },
                                    alignItems: { xs: 'stretch', md: 'center' },
                                    justifyContent: 'center'
                                }}>
                                    {/* Поле для ввода причины отклонения */}
                                    {hasRejectedRows.length > 0 && (
                                        <TextField
                                            size="small"
                                            multiline
                                            rows={3}
                                            placeholder="Причина отклонения..."
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            sx={{
                                                minWidth: { xs: '200px', md: '250px' },
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                                    color: darkMode ? '#ffffff' : '#333',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? '#9c27b0' : '#6a1b9a',
                                                        borderWidth: '2px'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? '#ab47bc' : '#7b1fa2',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: darkMode ? '#ce93d8' : '#8e24aa',
                                                        borderWidth: '2px'
                                                    }
                                                },
                                                '& .MuiInputBase-input': {
                                                    color: darkMode ? '#ffffff' : '#333',
                                                    fontSize: '0.75rem',
                                                    '&::placeholder': {
                                                        color: darkMode ? '#bbb' : '#666',
                                                        opacity: 1
                                                    }
                                                },
                                                '& .MuiInputLabel-root': {
                                                    color: darkMode ? '#9c27b0' : '#6a1b9a',
                                                    fontSize: '0.75rem',
                                                    '&.Mui-focused': {
                                                        color: darkMode ? '#ce93d8' : '#8e24aa'
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="small"
                                        onClick={handleCancelChanges}
                                        sx={{
                                            borderColor: darkMode ? '#f44336' : '#d32f2f',
                                            color: darkMode ? '#f44336' : '#d32f2f',
                                            px: 1.5,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(211, 47, 47, 0.1)',
                                                borderColor: darkMode ? '#f44336' : '#d32f2f',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Отменить
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="small"
                                        startIcon={<SaveIcon />}
                                        onClick={handleSaveChanges}
                                        disabled={hasRejectedRows.length > 0 && !rejectionReason.trim()}
                                        sx={{
                                            bgcolor: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                ? (darkMode ? '#666' : '#ccc')
                                                : '#4caf50',
                                            color: 'white',
                                            px: 1.5,
                                            py: 0.5,
                                            textTransform: 'none',
                                            fontSize: '0.75rem',
                                            fontWeight: 600,
                                            borderRadius: 1.5,
                                            boxShadow: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                ? 'none'
                                                : '0 2px 4px rgba(76, 175, 80, 0.3)',
                                            '&:hover': {
                                                bgcolor: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                    ? (darkMode ? '#666' : '#ccc')
                                                    : '#388e3c',
                                                transform: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                    ? 'none'
                                                    : 'translateY(-1px)',
                                                boxShadow: hasRejectedRows.length > 0 && !rejectionReason.trim()
                                                    ? 'none'
                                                    : '0 4px 8px rgba(76, 175, 80, 0.4)'
                                            },
                                            '&:disabled': {
                                                bgcolor: darkMode ? '#666' : '#ccc',
                                                color: darkMode ? '#bbb' : '#999',
                                                cursor: 'not-allowed'
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        Сохранить
                                    </Button>
                                </Box>
                            )}

                            {/* Кнопка редактирования */}
                            {/* {user.roles?.includes('project_manager') && (
                                <Button
                                    variant={editMode ? "contained" : "outlined"}
                                    startIcon={<EditIcon />}
                                    onClick={() => {
                                        if (editMode && editedRows.length > 0) {
                                            if (window.confirm('У вас есть несохраненные изменения. Выйти из режима редактирования?')) {
                                                setEditMode(false);
                                                setSelectedRows(new Set());
                                                // setEditedRows([]);
                                                handleCancelChanges()
                                            }
                                        } else {
                                            setEditMode(!editMode);
                                            if (!editMode) {
                                                // Входим в режим редактирования
                                                setSelectedRows(new Set());
                                            } else {
                                                // Выходим из режима редактирования
                                                setSelectedRows(new Set());
                                            }
                                        }
                                    }}
                                    sx={{
                                        bgcolor: editMode ? '#1976d2' : 'transparent',
                                        color: editMode ? 'white' : (darkMode ? '#64b5f6' : '#1976d2'),
                                        borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                        px: 1,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: editMode ? '0 2px 8px rgba(25, 118, 210, 0.3)' : 'none',
                                        '&:hover': {
                                            bgcolor: editMode ? '#1565c0' : (darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)'),
                                            transform: editMode ? 'translateY(-1px)' : 'none',
                                            boxShadow: editMode ? '0 4px 12px rgba(25, 118, 210, 0.4)' : 'none'
                                        },
                                        transition: 'all 0.2s ease',
                                        minWidth: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                    {editMode ? 'Завершить редактирование' : 'Редактировать'}
                                </Button>
                            )} */}

                            {/* Кнопка удаления выбранных строк */}
                            {/* {editMode && selectedRows.size > 0 && (
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleBulkDelete}
                                    sx={{
                                        bgcolor: '#f44336',
                                        color: 'white',
                                        px: 1,
                                        py: 0.5,
                                        textTransform: 'none',
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                        '&:hover': {
                                            bgcolor: '#d32f2f',
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.4)'
                                        },
                                        transition: 'all 0.2s ease',
                                        minWidth: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                    Удалить ({selectedRows.size})
                                </Button>
                            )} */}
                        </Box>
                    </Box>
                </Box>

                {/* Продвинутые фильтры */}
                {showAdvancedFilters && (
                    <AdvancedFiltersComponent
                        darkMode={darkMode}
                        advancedFilters={advancedFilters}
                        setAdvancedFilters={setAdvancedFilters}
                        users={users}
                        equipment={equipment}
                        stages={stages}
                        rulesMap={rulesMap}
                        statuses={statuses}
                        onApplyFilters={handleApplyAdvancedFilters}
                        onClearFilters={handleClearAdvancedFilters}
                        addFilter={addFilter}
                        removeFilter={removeFilter}
                        updateFilter={updateFilter}
                        getAvailableFiltersForSelect={getAvailableFiltersForSelect}
                        filterMode={filterMode}
                        handleFilterModeChange={handleFilterModeChange}
                    />
                )}

                {/* Таблица */}
                <Box sx={{
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    width: '100%',
                    minWidth: '100%',
                    // overflowX: 'auto',
                }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f5f7fa' : '#333',
                            gap: 2,
                        }}>
                            <CircularProgress size={40} sx={{ color: darkMode ? '#64b5f6' : '#1976d2' }} />
                            <Typography variant="h6">Загрузка данных...</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f44336' : '#d32f2f',
                        }}>
                            <Typography variant="h6">Ошибка загрузки: {error}</Typography>
                        </Box>
                    ) : (filteredData.length === 0 && groupedData.groupedData.length === 0) ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#bbb' : '#666',
                        }}>
                            <Typography variant="h6">
                                {searchQuery || advancedFilters.length > 0
                                    ? 'По вашему запросу ничего не найдено'
                                    : 'Нет данных для отображения'
                                }
                            </Typography>
                        </Box>
                    ) : (

                        <GroupedTableVirtuoso
                            key={'grouped-table'}
                            data={groupedData.groupedData}
                            groupCounts={groupedData.groupCounts}
                            totalCount={groupedData.groupedData.length}
                            components={VirtuosoTableComponents}
                            fixedHeaderContent={() => fixedHeaderContent(darkMode, editMode, selectedRows, filteredData, handleSelectAll, sortBy, sortDirection, handleSort, visibleColumns, columnWidths)}
                            groupContent={(index) => groupContent(index, groupedData, darkMode, user, activeAccoutingsProjects, rulesMap, visibleColumns)}
                            itemContent={(index, groupIndex) => {
                                const row = groupedData.groupedData[index];
                                if (!row) {
                                    console.warn('Missing row at index:', index);
                                    return null;
                                }
                                const cells = rowContent(index, row, {
                                    editMode,
                                    editing,
                                    saveEditedField: handleEditedField,
                                    setEditingField: handleChangeEdititng,
                                    darkMode,
                                    editedFields: editedRows.find(v => v.id === row.accounting.id)?.changes || {},
                                    selectedRows,
                                    handleRowSelection,
                                    rulesMap,
                                    canEditReady,
                                    canEditSuccess,
                                    visibleColumns
                                });

                                // Фильтруем ячейки по видимости столбцов
                                if (row.isPadding) {
                                    return cells; // Для padding элементов уже есть фильтрация внутри rowContent
                                }

                                // Проверяем, что cells существует и имеет правильную структуру
                                if (!cells) {
                                    return null; // Возвращаем null если cells не существует
                                }

                                if (!cells.props || !cells.props.children) {
                                    return cells; // Возвращаем как есть если структура неправильная
                                }

                                const cellsArray = React.Children.toArray(cells.props.children);
                                const visibleCells = cellsArray.filter((cell, index) => {
                                    return index < columns.length && visibleColumns[columns[index].id];
                                });

                                return React.cloneElement(cells, {}, visibleCells);
                            }}
                            style={{
                                backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                width: '100%',
                                height: '100%',
                                minHeight: '400px',
                            }}

                        />

                    )}
                </Box>
            </Paper >

            {/* <ObjectDialog
                open={objectDialogOpen}
                currentUser={user}
                onClose={handleCloseObjectDialog}
                objectData={selectedObject}
                darkMode={darkMode}
                responsible={responsible}
                equipment={equipment}
                stages={stages}
                projects={projects}
                selectedProjectId={selectedProjectId}
            /> */}
            {/* <AuditHistoryDialog
                open={historyDialogOpen}
                onClose={handleCloseHistoryDialog}
                tableName="objects_reestr"
                recordId={selectedObject?.id}
                recordTitle={selectedObject?.name}
                darkMode={darkMode}
                dataMap={
                    {
                        'responsible': { 'users': { key: 'id', value: 'name' } },
                        'coexecutors': { 'users': { key: 'id', value: 'name' } },
                        'equipment': { 'equipment': { key: 'id', value: 'name' } },
                        'project_id': { 'projects': { key: 'id', value: 'name' } }
                    }
                }
                data={
                    {
                        users: responsible,
                        equipment: equipment,
                        stages: stages,
                        projects: projects,
                    }
                }
                translates={
                    {
                        'fid': '№',
                        'responsible': 'Ответственный',
                        'coexecutors': 'Соисполнители',
                        'equipment': 'Оснащение',
                        'stages': 'Этапы',
                        'project_id': 'Проект',
                        'plan_start': 'Плановое начало',
                        'plan_end': 'Плановое завершение',
                        'fact_start': 'Фактическое начало',
                        'fact_end': 'Фактическое завершение',
                    }
                }
                formatting={
                    {
                        'plan_start': formatDateShort,
                        'plan_end': formatDateShort,
                        'fact_start': formatDateShort,
                        'fact_end': formatDateShort,
                    }
                }
                ignore={
                    ['modifier']
                }
            /> */}

            {/* Меню настройки видимости столбцов */}
            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={handleColumnMenuClose}
                PaperProps={{
                    sx: {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        minWidth: 250,
                        maxHeight: 400,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            color: darkMode ? '#f5f7fa' : '#333',
                            padding: '0',
                            pr: 1,
                            // margin: '2px 8px',
                            borderRadius: '6px',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            },
                            // '&:first-of-type': {
                            //     marginTop: '8px'
                            // },
                            // '&:last-of-type': {
                            //     marginBottom: '8px'
                            // }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Заголовок меню */}
                <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center', margin: '0 !important', padding: '0 !important', borderRadius: '0 !important' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkMode ? '#f5f7fa' : '#333' }}>
                        Видимость столбцов
                    </Typography>
                </MenuItem>

                <Divider sx={{
                    bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    margin: '4px 0'
                }} />

                {/* Кнопки для показа/скрытия всех столбцов */}
                {/* <MenuItem onClick={handleShowAllColumns}>
                    <Typography variant="body2" sx={{ color: darkMode ? '#4caf50' : '#2e7d32' }}>
                        Показать все столбцы
                    </Typography>
                </MenuItem>
                
                <MenuItem onClick={handleHideAllColumns}>
                    <Typography variant="body2" sx={{ color: darkMode ? '#f44336' : '#d32f2f' }}>
                        Скрыть все столбцы (кроме первого)
                    </Typography>
                </MenuItem> */}

                {/* <Divider sx={{ bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)' }} /> */}

                {/* Список столбцов с чекбоксами */}
                {columns.map((column, index) => (
                    <MenuItem
                        key={column.id}
                        onClick={(e) => e.stopPropagation()}
                        disabled={column.id === 'cost' && user.roles.includes('viewer')}
                    >
                        <Checkbox
                            checked={visibleColumns[column.id] || false}
                            onChange={(e) => handleColumnVisibilityChange(column.id, e.target.checked)}
                            size="small"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                '&.Mui-checked': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                mr: 0
                            }}
                        />
                        <ListItemText
                            primary={column.label}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                color: darkMode ? '#f5f7fa' : '#333'
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </Box >
    );
};

export default AccountingSheetView;
